// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { OperationOptions } from "@azure-rest/core-client";
import type { PagedAsyncIterableIterator } from "./static-helpers/pagingHelpers.js";
import type {
  AIFoundryModelCatalogName,
  AIServicesAccountKey,
  AsciiFoldingTokenFilter,
  AzureOpenAIModelName,
  CognitiveServicesAccount as BaseCognitiveServicesAccount,
  KnowledgeBaseModel as BaseKnowledgeBaseModel,
  SearchIndexerSkill as BaseSearchIndexerSkill,
  BinaryQuantizationCompression,
  BM25Similarity,
  CharFilterName,
  ChatCompletionExtraParametersBehavior,
  ChatCompletionResponseFormat,
  CjkBigramTokenFilter,
  ClassicSimilarity,
  ClassicTokenizer,
  CognitiveServicesAccountKey,
  CommonGramTokenFilter,
  CommonModelParameters,
  ConditionalSkill,
  ContentUnderstandingSkill,
  CorsOptions,
  CustomEntity,
  CustomNormalizer,
  DefaultCognitiveServicesAccount,
  DictionaryDecompounderTokenFilter,
  DistanceScoringFunction,
  DocumentExtractionSkill,
  DocumentIntelligenceLayoutSkillChunkingProperties,
  DocumentIntelligenceLayoutSkillExtractionOptions,
  DocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  DocumentIntelligenceLayoutSkillOutputFormat,
  DocumentIntelligenceLayoutSkillOutputMode,
  EdgeNGramTokenFilterSide,
  EdgeNGramTokenizer,
  ElisionTokenFilter,
  EntityLinkingSkill,
  EntityRecognitionSkillV3,
  FieldMapping,
  FreshnessScoringFunction,
  HighWaterMarkChangeDetectionPolicy,
  IndexingSchedule,
  IndexProjectionMode,
  KeepTokenFilter,
  KeywordMarkerTokenFilter,
  KnowledgeSourceContentExtractionMode,
  KnownBlobIndexerDataToExtract,
  KnownBlobIndexerImageAction,
  KnownBlobIndexerParsingMode,
  KnownBlobIndexerPDFTextRotationAlgorithm,
  KnownCharFilterName,
  KnownCustomEntityLookupSkillLanguage,
  // KnownEntityCategory,
  // KnownEntityRecognitionSkillLanguage,
  KnownImageAnalysisSkillLanguage,
  KnownImageDetail,
  KnownIndexerExecutionEnvironment,
  KnownKeyPhraseExtractionSkillLanguage,
  KnownLexicalAnalyzerName,
  KnownLexicalTokenizerName,
  KnownOcrSkillLanguage,
  KnownPIIDetectionSkillMaskingMode,
  KnownRegexFlags,
  KnownSearchFieldDataType,
  KnownSearchIndexerDataSourceType,
  // KnownSentimentSkillLanguage,
  KnownSplitSkillLanguage,
  KnownTextSplitMode,
  KnownTextTranslationSkillLanguage,
  KnownTokenFilterName,
  KnownVectorSearchAlgorithmKind,
  KnownVectorSearchAlgorithmMetric,
  KnownVisualFeature,
  LanguageDetectionSkill,
  LengthTokenFilter,
  LexicalAnalyzerName,
  LexicalNormalizerName,
  LexicalTokenizerName,
  LimitTokenFilter,
  LuceneStandardAnalyzer,
  MagnitudeScoringFunction,
  MappingCharFilter,
  MarkdownHeaderDepth,
  MarkdownParsingSubmode,
  MergeSkill,
  MicrosoftLanguageStemmingTokenizer,
  MicrosoftLanguageTokenizer,
  NativeBlobSoftDeleteDeletionDetectionPolicy,
  NGramTokenizer,
  OcrLineEnding,
  PathHierarchyTokenizerV2 as PathHierarchyTokenizer,
  PatternCaptureTokenFilter,
  PatternReplaceCharFilter,
  PatternReplaceTokenFilter,
  PhoneticTokenFilter,
  ScalarQuantizationCompression,
  ScoringFunctionAggregation,
  SearchAlias,
  SearchIndexerDataContainer,
  SearchIndexerDataNoneIdentity,
  SearchIndexerDataUserAssignedIdentity,
  SearchIndexerIndexProjectionSelector,
  SearchIndexerKnowledgeStoreProjection,
  SearchIndexKnowledgeSourceParameters,
  SearchSuggester,
  SemanticSearch,
  SentimentSkillV3,
  ServiceCounters,
  ServiceLimits,
  ShaperSkill,
  ShingleTokenFilter,
  SnowballTokenFilter,
  SoftDeleteColumnDeletionDetectionPolicy,
  SqlIntegratedChangeTrackingPolicy,
  StemmerOverrideTokenFilter,
  StemmerTokenFilter,
  StopAnalyzer,
  StopwordsTokenFilter,
  SynonymTokenFilter,
  TagScoringFunction,
  TextWeights,
  TokenFilterName,
  TruncateTokenFilter,
  UaxUrlEmailTokenizer,
  UniqueTokenFilter,
  VectorEncodingFormat,
  VectorSearchProfile,
  VectorSearchVectorizerKind,
  WebKnowledgeSourceParameters,
  WordDelimiterTokenFilter,
} from "./models/azure/search/documents/indexes/index.js";
import type {
  AIServices,
  KnowledgeSourceVectorizer as BaseKnowledgeSourceVectorizer,
} from "./models/azure/search/documents/knowledgeBases/index.js";
import type { KnowledgeBase } from "./knowledgeBaseModels.js";

/**
 * Options for a list skillsets operation.
 */
export type ListSkillsetsOptions = OperationOptions;

/**
 * Options for a list synonymMaps operation.
 */
export type ListSynonymMapsOptions = OperationOptions;

/**
 * Options for a list indexes operation.
 */
export type ListIndexesOptions = OperationOptions;

/**
 * Options for a list indexers operation.
 */
export type ListIndexersOptions = OperationOptions;

/**
 * Options for a list data sources operation.
 */
export type ListDataSourceConnectionsOptions = OperationOptions;

/**
 * Options for get index operation.
 */
export type GetIndexOptions = OperationOptions;

/**
 * Options for get skillset operation.
 */
export type GetSkillSetOptions = OperationOptions;

/**
 * Options for get synonymmaps operation.
 */
export type GetSynonymMapsOptions = OperationOptions;

/**
 * Options for get indexer operation.
 */
export type GetIndexerOptions = OperationOptions;

/**
 * Options for get datasource operation.
 */
export type GetDataSourceConnectionOptions = OperationOptions;

/**
 * Options for get index statistics operation.
 */
export type GetIndexStatisticsOptions = OperationOptions;

/**
 * Statistics for a given index. Statistics are collected periodically and are not guaranteed to
 * always be up-to-date.
 */
export interface SearchIndexStatistics {
  /**
   * The number of documents in the index.
   * **NOTE: This property will not be serialized. It can only be populated by the server.**
   */
  readonly documentCount: number;
  /**
   * The amount of storage in bytes consumed by the index.
   * **NOTE: This property will not be serialized. It can only be populated by the server.**
   */
  readonly storageSize: number;
  /**
   * The amount of memory in bytes consumed by vectors in the index.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly vectorIndexSize: number;
}

/**
 * Response from a get service statistics request. If successful, it includes service level
 * counters and limits.
 */
export interface SearchServiceStatistics {
  /**
   * Service level resource counters.
   */
  counters: ServiceCounters;
  /**
   * Service level general limits.
   */
  limits: ServiceLimits;
}

/**
 * Options for get service statistics operation.
 */
export type GetServiceStatisticsOptions = OperationOptions;

/**
 * Options for get indexer status operation.
 */
export type GetIndexerStatusOptions = OperationOptions;

/**
 * Options for reset indexer operation.
 */
export type ResetIndexerOptions = OperationOptions;

/**
 * Options for run indexer operation.
 */
export type RunIndexerOptions = OperationOptions;

/**
 * Options for create index operation.
 */
export type CreateIndexOptions = OperationOptions;

/**
 * Options for create skillset operation.
 */
export type CreateSkillsetOptions = OperationOptions;

/**
 * Options for create alias operation.
 */
export type CreateAliasOptions = OperationOptions;

/**
 * Options for create or update alias operation.
 */
export interface CreateOrUpdateAliasOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for delete alias operation.
 */
export interface DeleteAliasOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for get alias operation.
 */
export type GetAliasOptions = OperationOptions;

/**
 * Options for list aliases operation.
 */
export type ListAliasesOptions = OperationOptions;

/**
 * Search Alias object.
 */
export type SearchIndexAlias = SearchAlias;

/**
 * Options for create synonymmap operation.
 */
export type CreateSynonymMapOptions = OperationOptions;

/**
 * Options for create indexer operation.
 */
export type CreateIndexerOptions = OperationOptions;

/**
 * Options for create datasource operation.
 */
export type CreateDataSourceConnectionOptions = OperationOptions;

/**
 * Options for create/update index operation.
 */
export interface CreateOrUpdateIndexOptions extends OperationOptions {
  /**
   * Allows new analyzers, tokenizers, token filters, or char filters to be added to an index by
   * taking the index offline for at least a few seconds. This temporarily causes indexing and
   * query requests to fail. Performance and write availability of the index can be impaired for
   * several minutes after the index is updated, or longer for very large indexes.
   */
  allowIndexDowntime?: boolean;
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for create/update skillset operation.
 */
export interface CreateOrUpdateSkillsetOptions extends OperationOptions {
  /**
   * If set to true, Resource will be updated only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
  /**
   * Ignores cache reset requirements.
   */
  skipIndexerResetRequirementForCache?: boolean;
  /**
   * Disables cache reprocessing change detection.
   */
  disableCacheReprocessingChangeDetection?: boolean;
}

/**
 * Options for create/update synonymmap operation.
 */
export interface CreateOrUpdateSynonymMapOptions extends OperationOptions {
  /**
   * If set to true, Resource will be updated only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for create/update indexer operation.
 */
export interface CreateorUpdateIndexerOptions extends OperationOptions {
  /**
   * If set to true, Resource will be updated only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
  /**
   * Ignores cache reset requirements.
   */
  skipIndexerResetRequirementForCache?: boolean;
  /**
   * Disables cache reprocessing change detection.
   */
  disableCacheReprocessingChangeDetection?: boolean;
}

/**
 * Options for create/update datasource operation.
 */
export interface CreateorUpdateDataSourceConnectionOptions extends OperationOptions {
  /**
   * If set to true, Resource will be updated only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
  /**
   * Ignores cache reset requirements.
   */
  skipIndexerResetRequirementForCache?: boolean;
}

/**
 * Options for delete index operation.
 */
export interface DeleteIndexOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for delete skillset operaion.
 */
export interface DeleteSkillsetOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for delete synonymmap operation.
 */
export interface DeleteSynonymMapOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for delete indexer operation.
 */
export interface DeleteIndexerOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Options for delete datasource operation.
 */
export interface DeleteDataSourceConnectionOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}

/**
 * Specifies some text and analysis components used to break that text into tokens.
 */
export interface AnalyzeRequest {
  /**
   * The text to break into tokens.
   */
  text: string;
  /**
   * The name of the analyzer to use to break the given text. If this parameter is not specified,
   * you must specify a tokenizer instead. The tokenizer and analyzer parameters are mutually
   * exclusive. {@link KnownAnalyzerNames} is an enum containing built-in analyzer names.
   * NOTE: Either analyzerName or tokenizerName is required in an AnalyzeRequest.
   */
  analyzerName?: LexicalAnalyzerName;
  /**
   * The name of the tokenizer to use to break the given text. If this parameter is not specified,
   * you must specify an analyzer instead. The tokenizer and analyzer parameters are mutually
   * exclusive. {@link KnownTokenizerNames} is an enum containing built-in tokenizer names.
   * NOTE: Either analyzerName or tokenizerName is required in an AnalyzeRequest.
   */
  tokenizerName?: LexicalTokenizerName;
  /**
   * The name of the normalizer to use to normalize the given text. {@link KnownNormalizerNames} is
   * an enum containing built-in analyzer names.
   */
  normalizerName?: LexicalNormalizerName;
  /**
   * An optional list of token filters to use when breaking the given text. This parameter can only
   * be set when using the tokenizer parameter.
   */
  tokenFilters?: TokenFilterName[];
  /**
   * An optional list of character filters to use when breaking the given text. This parameter can
   * only be set when using the tokenizer parameter.
   */
  charFilters?: CharFilterName[];
}

/**
 * Options for analyze text operation.
 */
export type AnalyzeTextOptions = OperationOptions & AnalyzeRequest;

// BEGIN manually modified generated interfaces
//
// This section is for places where we have to manually fix issues
// with interfaces from the generated code.
// One issue is that unions of discriminated types generated with
// their abstract base class as a member.

/**
 * Flexibly separates text into terms via a regular expression pattern. This analyzer is
 * implemented using Apache Lucene.
 */
export interface PatternAnalyzer {
  /**
   * Polymorphic Discriminator
   */
  odatatype: "#Microsoft.Azure.Search.PatternAnalyzer";
  /**
   * The name of the analyzer. It must only contain letters, digits, spaces, dashes or underscores,
   * can only start and end with alphanumeric characters, and is limited to 128 characters.
   */
  name: string;
  /**
   * A value indicating whether terms should be lower-cased. Default is true. Default value: true.
   */
  lowerCaseTerms?: boolean;
  /**
   * A regular expression pattern to match token separators. Default is an expression that matches
   * one or more whitespace characters. Default value: `\W+`.
   */
  pattern?: string;
  /**
   * Regular expression flags. Possible values include: 'CANON_EQ', 'CASE_INSENSITIVE', 'COMMENTS',
   * 'DOTALL', 'LITERAL', 'MULTILINE', 'UNICODE_CASE', 'UNIX_LINES'
   */
  flags?: RegexFlags[];
  /**
   * A list of stopwords.
   */
  stopwords?: string[];
}

/**
 * Allows you to take control over the process of converting text into indexable/searchable tokens.
 * It's a user-defined configuration consisting of a single predefined tokenizer and one or more
 * filters. The tokenizer is responsible for breaking text into tokens, and the filters for
 * modifying tokens emitted by the tokenizer.
 */
export interface CustomAnalyzer {
  /**
   * Polymorphic Discriminator
   */
  odatatype: "#Microsoft.Azure.Search.CustomAnalyzer";
  /**
   * The name of the analyzer. It must only contain letters, digits, spaces, dashes or underscores,
   * can only start and end with alphanumeric characters, and is limited to 128 characters.
   */
  name: string;
  /**
   * The name of the tokenizer to use to divide continuous text into a sequence of tokens, such as
   * breaking a sentence into words. {@link KnownTokenizerNames} is an enum containing built-in
   * tokenizer names.
   */
  tokenizerName: LexicalTokenizerName;
  /**
   * A list of token filters used to filter out or modify the tokens generated by a tokenizer. For
   * example, you can specify a lowercase filter that converts all characters to lowercase. The
   * filters are run in the order in which they are listed.
   */
  tokenFilters?: TokenFilterName[];
  /**
   * A list of character filters used to prepare input text before it is processed by the
   * tokenizer. For instance, they can replace certain characters or symbols. The filters are run
   * in the order in which they are listed.
   */
  charFilters?: CharFilterName[];
}

/**
 * Contains the possible cases for Analyzer.
 */
export type LexicalAnalyzer =
  | CustomAnalyzer
  | PatternAnalyzer
  | LuceneStandardAnalyzer
  | StopAnalyzer;

/**
 * A skill that calls a language model via Azure AI Foundry's Chat Completions endpoint.
 */
export interface ChatCompletionSkill extends WebApiSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Custom.ChatCompletionSkill";
  /**
   * API key for authenticating to the model. Both apiKey and authIdentity cannot be specified at
   * the same time.
   */
  apiKey?: string;
  /**
   * Common language model parameters that customers can tweak. If omitted, reasonable defaults will
   * be applied.
   */
  commonModelParameters?: CommonModelParameters;
  /**
   * Open-type dictionary for model-specific parameters that should be appended to the chat
   * completions call. Follows Azure AI Foundry’s extensibility pattern.
   */
  extraParameters?: { [propertyName: string]: unknown };
  /**
   * How extra parameters are handled by Azure AI Foundry. Default is 'error'.
   */
  extraParametersBehavior?: ChatCompletionExtraParametersBehavior;
  /**
   * Determines how the LLM should format its response. Defaults to 'text' response type.
   */
  responseFormat?: ChatCompletionResponseFormat;
}

/**
 * A skill that can call a Web API endpoint, allowing you to extend a skillset by having it call
 * your custom code.
 */
export interface WebApiSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype:
    | "#Microsoft.Skills.Custom.WebApiSkill"
    | "#Microsoft.Skills.Custom.ChatCompletionSkill";
  /**
   * The url for the Web API.
   */
  uri: string;
  /**
   * The headers required to make the http request.
   */
  httpHeaders?: { [propertyName: string]: string };
  /**
   * The method for the http request.
   */
  httpMethod?: string;
  /**
   * The desired timeout for the request. Default is 30 seconds.
   */
  timeout?: string;
  /**
   * The desired batch size which indicates number of documents.
   */
  batchSize?: number;
  /**
   * If set, the number of parallel calls that can be made to the Web API.
   */
  degreeOfParallelism?: number;
  /**
   * Applies to custom skills that connect to external code in an Azure function or some other
   * application that provides the transformations. This value should be the application ID created
   * for the function or app when it was registered with Azure Active Directory. When specified, the
   * custom skill connects to the function or app using a managed ID (either system or
   * user-assigned) of the search service and the access token of the function or app, using this
   * value as the resource id for creating the scope of the access token.
   */
  authResourceId?: string;
  /**
   * The user-assigned managed identity used for outbound connections. If an authResourceId is
   * provided and it's not specified, the system-assigned managed identity is used. On updates to
   * the indexer, if the identity is unspecified, the value remains unchanged. If set to "none", the
   * value of this property is cleared.
   */
  authIdentity?: SearchIndexerDataIdentity;
}

export type WebApiSkills = WebApiSkill | ChatCompletionSkill;

/**
 * Contains the possible cases for Skill.
 */
export type SearchIndexerSkill =
  | AzureOpenAIEmbeddingSkill
  | ConditionalSkill
  | CustomEntityLookupSkill
  | DocumentExtractionSkill
  | DocumentIntelligenceLayoutSkill
  | ContentUnderstandingSkill
  | EntityLinkingSkill
  | EntityRecognitionSkill
  | EntityRecognitionSkillV3
  | ImageAnalysisSkill
  | KeyPhraseExtractionSkill
  | LanguageDetectionSkill
  | MergeSkill
  | OcrSkill
  | PIIDetectionSkill
  | SentimentSkill
  | SentimentSkillV3
  | ShaperSkill
  | SplitSkill
  | TextTranslationSkill
  | WebApiSkills;

/**
 * A skill that extracts content and layout information (as markdown), via Azure AI Services, from
 * files within the enrichment pipeline.
 */
export interface DocumentIntelligenceLayoutSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Util.DocumentIntelligenceLayoutSkill";
  /**
   * Controls the cardinality of the output format. Default is 'markdown'.
   */
  outputFormat?: DocumentIntelligenceLayoutSkillOutputFormat;
  /**
   * Controls the cardinality of the output produced by the skill. Default is 'oneToMany'.
   */
  outputMode?: DocumentIntelligenceLayoutSkillOutputMode;
  /**
   * The depth of headers in the markdown output. Default is h6.
   */
  markdownHeaderDepth?: DocumentIntelligenceLayoutSkillMarkdownHeaderDepth;
  /**
   * Controls the cardinality of the content extracted from the document by the skill
   */
  extractionOptions?: DocumentIntelligenceLayoutSkillExtractionOptions[];
  /**
   * Controls the cardinality for chunking the content.
   */
  chunkingProperties?: DocumentIntelligenceLayoutSkillChunkingProperties;
}

/**
 * Contains the possible cases for CognitiveServicesAccount.
 */
export type CognitiveServicesAccount =
  | DefaultCognitiveServicesAccount
  | CognitiveServicesAccountKey
  | AIServicesAccountKey
  | AIServicesAccountIdentity;

/**
 * The multi-region account of an Azure AI service resource that's attached to a skillset.
 */
export interface AIServicesAccountIdentity extends BaseCognitiveServicesAccount {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Azure.Search.AIServicesByIdentity";
  /**
   * The user-assigned managed identity used for connections to AI Service. If not specified, the
   * system-assigned managed identity is used. On updates to the skillset, if the identity is
   * unspecified, the value remains unchanged. If set to "none", the value of this property is
   * cleared.
   */
  identity?: SearchIndexerDataIdentity;
  /**
   * The subdomain url for the corresponding AI Service.
   */
  subdomainUrl: string;
}
/**
 * Tokenizer that uses regex pattern matching to construct distinct tokens. This tokenizer is
 * implemented using Apache Lucene.
 */
export interface PatternTokenizer {
  /**
   * Polymorphic Discriminator
   */
  odatatype: "#Microsoft.Azure.Search.PatternTokenizer";
  /**
   * The name of the tokenizer. It must only contain letters, digits, spaces, dashes or
   * underscores, can only start and end with alphanumeric characters, and is limited to 128
   * characters.
   */
  name: string;
  /**
   * A regular expression pattern to match token separators. Default is an expression that matches
   * one or more whitespace characters. Default value: `\W+`.
   */
  pattern?: string;
  /**
   * Regular expression flags. Possible values include: 'CANON_EQ', 'CASE_INSENSITIVE', 'COMMENTS',
   * 'DOTALL', 'LITERAL', 'MULTILINE', 'UNICODE_CASE', 'UNIX_LINES'
   */
  flags?: RegexFlags[];
  /**
   * The zero-based ordinal of the matching group in the regular expression pattern to extract into
   * tokens. Use -1 if you want to use the entire pattern to split the input into tokens,
   * irrespective of matching groups. Default is -1. Default value: -1.
   */
  group?: number;
}
/**
 * Breaks text following the Unicode Text Segmentation rules. This tokenizer is implemented using
 * Apache Lucene.
 */
export interface LuceneStandardTokenizer {
  /**
   * Polymorphic Discriminator
   */
  odatatype:
    | "#Microsoft.Azure.Search.StandardTokenizerV2"
    | "#Microsoft.Azure.Search.StandardTokenizer";
  /**
   * The name of the tokenizer. It must only contain letters, digits, spaces, dashes or
   * underscores, can only start and end with alphanumeric characters, and is limited to 128
   * characters.
   */
  name: string;
  /**
   * The maximum token length. Default is 255. Tokens longer than the maximum length are split. The
   * maximum token length that can be used is 300 characters. Default value: 255.
   */
  maxTokenLength?: number;
}

/**
 * Generates n-grams of the given size(s) starting from the front or the back of an input token.
 * This token filter is implemented using Apache Lucene.
 */
export interface EdgeNGramTokenFilter {
  /**
   * Polymorphic Discriminator
   */
  odatatype:
    | "#Microsoft.Azure.Search.EdgeNGramTokenFilterV2"
    | "#Microsoft.Azure.Search.EdgeNGramTokenFilter";
  /**
   * The name of the token filter. It must only contain letters, digits, spaces, dashes or
   * underscores, can only start and end with alphanumeric characters, and is limited to 128
   * characters.
   */
  name: string;
  /**
   * The minimum n-gram length. Default is 1. Maximum is 300. Must be less than the value of
   * maxGram. Default value: 1.
   */
  minGram?: number;
  /**
   * The maximum n-gram length. Default is 2. Maximum is 300. Default value: 2.
   */
  maxGram?: number;
  /**
   * Specifies which side of the input the n-gram should be generated from. Default is "front".
   * Possible values include: 'Front', 'Back'
   */
  side?: EdgeNGramTokenFilterSide;
}

/**
 * Emits the entire input as a single token. This tokenizer is implemented using Apache Lucene.
 */
export interface KeywordTokenizer {
  /**
   * Polymorphic Discriminator
   */
  odatatype:
    | "#Microsoft.Azure.Search.KeywordTokenizerV2"
    | "#Microsoft.Azure.Search.KeywordTokenizer";
  /**
   * The name of the tokenizer. It must only contain letters, digits, spaces, dashes or
   * underscores, can only start and end with alphanumeric characters, and is limited to 128
   * characters.
   */
  name: string;
  /**
   * The maximum token length. Default is 256. Tokens longer than the maximum length are split. The
   * maximum token length that can be used is 300 characters. Default value: 256.
   */
  maxTokenLength?: number;
}

/**
 * Contains the possible cases for Tokenizer.
 */
export type LexicalTokenizer =
  | ClassicTokenizer
  | EdgeNGramTokenizer
  | KeywordTokenizer
  | MicrosoftLanguageTokenizer
  | MicrosoftLanguageStemmingTokenizer
  | NGramTokenizer
  | PathHierarchyTokenizer
  | PatternTokenizer
  | LuceneStandardTokenizer
  | UaxUrlEmailTokenizer;

/**
 *  Definition of additional projections to azure blob, table, or files, of enriched data.
 */
export interface SearchIndexerKnowledgeStore {
  /**
   * The connection string to the storage account projections will be stored in.
   */
  storageConnectionString: string;
  /**
   * A list of additional projections to perform during indexing.
   */
  projections: SearchIndexerKnowledgeStoreProjection[];
  /**
   * The user-assigned managed identity used for connections to Azure Storage when writing
   * knowledge store projections. If the connection string indicates an identity (ResourceId) and
   * it's not specified, the system-assigned managed identity is used. On updates to the indexer,
   * if the identity is unspecified, the value remains unchanged. If set to "none", the value of
   * this property is cleared.
   */
  identity?: SearchIndexerDataIdentity;
}

/**
 * Contains the possible cases for Similarity.
 */
export type SimilarityAlgorithm = ClassicSimilarity | BM25Similarity;

/**
 * Generates n-grams of the given size(s). This token filter is implemented using Apache Lucene.
 */
export interface NGramTokenFilter {
  /**
   * Polymorphic Discriminator
   */
  odatatype:
    | "#Microsoft.Azure.Search.NGramTokenFilterV2"
    | "#Microsoft.Azure.Search.NGramTokenFilter";
  /**
   * The name of the token filter. It must only contain letters, digits, spaces, dashes or
   * underscores, can only start and end with alphanumeric characters, and is limited to 128
   * characters.
   */
  name: string;
  /**
   * The minimum n-gram length. Default is 1. Maximum is 300. Must be less than the value of
   * maxGram. Default value: 1.
   */
  minGram?: number;
  /**
   * The maximum n-gram length. Default is 2. Maximum is 300. Default value: 2.
   */
  maxGram?: number;
}

/**
 * Contains the possible cases for TokenFilter.
 */
export type TokenFilter =
  | AsciiFoldingTokenFilter
  | CjkBigramTokenFilter
  | CommonGramTokenFilter
  | DictionaryDecompounderTokenFilter
  | EdgeNGramTokenFilter
  | ElisionTokenFilter
  | KeepTokenFilter
  | KeywordMarkerTokenFilter
  | LengthTokenFilter
  | LimitTokenFilter
  | NGramTokenFilter
  | PatternCaptureTokenFilter
  | PatternReplaceTokenFilter
  | PhoneticTokenFilter
  | ShingleTokenFilter
  | SnowballTokenFilter
  | StemmerTokenFilter
  | StemmerOverrideTokenFilter
  | StopwordsTokenFilter
  | SynonymTokenFilter
  | TruncateTokenFilter
  | UniqueTokenFilter
  | WordDelimiterTokenFilter;

/**
 * Contains the possible cases for CharFilter.
 */
export type CharFilter = MappingCharFilter | PatternReplaceCharFilter;

/**
 * Contains the possible cases for LexicalNormalizer.
 */
export type LexicalNormalizer = CustomNormalizer;

/**
 * Contains the possible cases for ScoringFunction.
 */
export type ScoringFunction =
  | DistanceScoringFunction
  | FreshnessScoringFunction
  | MagnitudeScoringFunction
  | TagScoringFunction;

/**
 * Defines values for ComplexDataType.
 * Possible values include: 'Edm.ComplexType', 'Collection(Edm.ComplexType)'
 * @readonly
 */
export type ComplexDataType = "Edm.ComplexType" | "Collection(Edm.ComplexType)";

/**
 * Represents a field in an index definition, which describes the name, data type, and search
 * behavior of a field.
 */
export type SearchField = SimpleField | ComplexField;

/**
 * Represents a field in an index definition, which describes the name, data type, and search
 * behavior of a field.
 */
export interface SimpleField {
  /**
   * The name of the field, which must be unique within the fields collection of the index or
   * parent field.
   */
  name: string;
  /**
   * The data type of the field.
   */
  type: SearchFieldDataType;
  /**
   * A value indicating whether the field uniquely identifies documents in the index. Exactly one
   * top-level field in each index must be chosen as the key field and it must be of type
   * Edm.String. Key fields can be used to look up documents directly and update or delete specific
   * documents. Default is false.
   */
  key?: boolean;
  /**
   * A value indicating whether the field can be returned in a search result. You can disable this
   * option if you want to use a field (for example, margin) as a filter, sorting, or scoring
   * mechanism but do not want the field to be visible to the end user. This property must be false
   * for key fields. This property can be changed on existing fields. Enabling this property does
   * not cause any increase in index storage requirements. Default is true for vector fields, false
   * otherwise.
   */
  hidden?: boolean;
  /**
   * An immutable value indicating whether the field will be persisted separately on disk to be
   * returned in a search result. You can disable this option if you don't plan to return the field
   * contents in a search response to save on storage overhead. This can only be set during index
   * creation and only for vector fields. This property cannot be changed for existing fields or set
   * as false for new fields. If this property is set to `false`, the property `hidden` must be set
   * to `true`. This property must be true or unset for key fields, for new fields, and for
   * non-vector fields, and it must be null for complex fields. Disabling this property will reduce
   * index storage requirements. The default is true for vector fields.
   */
  stored?: boolean;
  /**
   * A value indicating whether the field is full-text searchable. This means it will undergo
   * analysis such as word-breaking during indexing. If you set a searchable field to a value like
   * "sunny day", internally it will be split into the individual tokens "sunny" and "day". This
   * enables full-text searches for these terms. Fields of type Edm.String or Collection(Edm.String)
   * are searchable by default. This property must be false for simple
   * fields of other non-string data types.
   * Note: searchable fields consume extra space in your index to accommodate additional tokenized
   * versions of the field value for full-text searches. If you want to save space in your index and
   * you don't need a field to be included in searches, set searchable to false. Default is false.
   */
  searchable?: boolean;
  /**
   * A value indicating whether to enable the field to be referenced in $filter queries.
   * `filterable` differs from `searchable` in how strings are handled. Fields of type Edm.String or
   * Collection(Edm.String) that are filterable do not undergo word-breaking, so comparisons are for
   * exact matches only. For example, if you set such a field f to "sunny day", $filter=f eq 'sunny'
   * will find no matches, but $filter=f eq 'sunny day' will. Default is false.
   */
  filterable?: boolean;
  /**
   * A value indicating whether to enable the field to be referenced in $orderby expressions. By
   * default, the service sorts results by score, but in many experiences users will want
   * to sort by fields in the documents. A simple field can be sortable only if it is single-valued
   * (it has a single value in the scope of the parent document). Simple collection fields cannot
   * be sortable, since they are multi-valued. Simple sub-fields of complex collections are also
   * multi-valued, and therefore cannot be sortable. This is true whether it's an immediate parent
   * field, or an ancestor field, that's the complex collection. The default is false.
   */
  sortable?: boolean;
  /**
   * A value indicating whether to enable the field to be referenced in facet queries. Typically
   * used in a presentation of search results that includes hit count by category (for example,
   * search for digital cameras and see hits by brand, by megapixels, by price, and so on).
   * Fields of type Edm.GeographyPoint or Collection(Edm.GeographyPoint) cannot be facetable.
   * Default is false for all other simple fields.
   */
  facetable?: boolean;
  /**
   * The name of the analyzer to use for the field. This option can be used only with
   * searchable fields and it can't be set together with either searchAnalyzer or indexAnalyzer.
   * Once the analyzer is chosen, it cannot be changed for the field.
   */
  analyzerName?: LexicalAnalyzerName;
  /**
   * The name of the analyzer used at search time for the field. This option can be used only with
   * searchable fields. It must be set together with `indexAnalyzerName` and it cannot be set
   * together with the `analyzerName` option. This property cannot be set to the name of a language
   * analyzer; use the `analyzerName` property instead if you need a language analyzer. This
   * analyzer can be updated on an existing field.
   */
  searchAnalyzerName?: LexicalAnalyzerName;
  /**
   * The name of the analyzer used at indexing time for the field. This option can be used only with
   * searchable fields. It must be set together with searchAnalyzer and it cannot be set together
   * with the `analyzerName` option. Once the analyzer is chosen, it cannot be changed for the
   * field. KnownAnalyzerNames is an enum containing known values.
   */
  indexAnalyzerName?: LexicalAnalyzerName;
  /**
   * A list of the names of synonym maps to associate with this field. This option can be used only
   * with searchable fields. Currently only one synonym map per field is supported. Assigning a
   * synonym map to a field ensures that query terms targeting that field are expanded at
   * query-time using the rules in the synonym map. This attribute can be changed on existing
   * fields.
   */
  synonymMapNames?: string[];
  /**
   * The name of the normalizer used at indexing time for the field.
   */
  normalizerName?: LexicalNormalizerName;
  /**
   * The dimensionality of the vector field.
   */
  vectorSearchDimensions?: number;
  /**
   * The name of the vector search profile that specifies the algorithm and vectorizer to use when
   * searching the vector field.
   */
  vectorSearchProfileName?: string;
  /**
   * The encoding format to interpret the field contents.
   */
  vectorEncodingFormat?: VectorEncodingFormat;
  /** A value indicating whether the field should be used for sensitivity label filtering. This enables document-level filtering based on Microsoft Purview sensitivity labels. */
  hasSensitivityLabel?: boolean;
}

export function isComplexField(field: SearchField): field is ComplexField {
  return field.type === "Edm.ComplexType" || field.type === "Collection(Edm.ComplexType)";
}

/**
 * Represents a field in an index definition, which describes the name, data type, and search
 * behavior of a field.
 */
export interface ComplexField {
  /**
   * The name of the field, which must be unique within the fields collection of the index or
   * parent field.
   */
  name: string;
  /**
   * The data type of the field.
   * Possible values include: 'Edm.ComplexType','Collection(Edm.ComplexType)'
   */
  type: ComplexDataType;
  /**
   * A list of sub-fields.
   */
  fields?: SearchField[];
}

/**
 * Represents a synonym map definition.
 */
export interface SynonymMap {
  /**
   * The name of the synonym map.
   */
  name: string;
  /**
   * An array of synonym rules in the specified synonym map format.
   */
  synonyms: string[];
  /**
   * A description of an encryption key that you create in Azure Key Vault. This key is used to
   * provide an additional level of encryption-at-rest for your data when you want full assurance
   * that no one, not even Microsoft, can decrypt your data in Azure AI Search. Once you
   * have encrypted your data, it will always remain encrypted. Azure AI Search will ignore
   * attempts to set this property to null. You can change this property as needed if you want to
   * rotate your encryption key; Your data will be unaffected. Encryption with customer-managed
   * keys is not available for free search services, and is only available for paid services
   * created on or after January 1, 2019.
   */
  encryptionKey?: SearchResourceEncryptionKey;
  /**
   * The ETag of the synonym map.
   */
  etag?: string;
}

/**
 * An iterator for listing the indexes that exist in the Search service. Will make requests
 * as needed during iteration. Use .byPage() to make one request to the server
 * per iteration.
 */
export type IndexIterator = PagedAsyncIterableIterator<SearchIndex, SearchIndex[], {}>;

/**
 * An iterator for listing the knowledge bases that exist in the Search service. Will make requests
 * as needed during iteration. Use .byPage() to make one request to the server per iteration.
 */
export type KnowledgeBaseIterator = PagedAsyncIterableIterator<KnowledgeBase, KnowledgeBase[], {}>;

/**
 * An iterator for listing the knowledge sources that exist in the Search service. Will make requests
 * as needed during iteration. Use .byPage() to make one request to the server per iteration.
 */
export type KnowledgeSourceIterator = PagedAsyncIterableIterator<
  KnowledgeSource,
  KnowledgeSource[],
  {}
>;

/**
 * An iterator for listing the aliases that exist in the Search service. This will make requests
 * as needed during iteration. Use .byPage() to make one request to the server
 * per iteration.
 */
export type AliasIterator = PagedAsyncIterableIterator<SearchIndexAlias, SearchIndexAlias[], {}>;

/**
 * An iterator for listing the indexes that exist in the Search service. Will make requests
 * as needed during iteration. Use .byPage() to make one request to the server
 * per iteration.
 */
export type IndexNameIterator = PagedAsyncIterableIterator<string, string[], {}>;

/**
 * Represents a search index definition, which describes the fields and search behavior of an
 * index.
 */
export interface SearchIndex {
  /**
   * The name of the index.
   */
  name: string;
  /**
   * The description of the index.
   */
  description?: string;
  /**
   * The fields of the index.
   */
  fields: SearchField[];
  /**
   * The scoring profiles for the index.
   */
  scoringProfiles?: ScoringProfile[];
  /**
   * The name of the scoring profile to use if none is specified in the query. If this property is
   * not set and no scoring profile is specified in the query, then default scoring (tf-idf) will
   * be used.
   */
  defaultScoringProfile?: string;
  /**
   * Options to control Cross-Origin Resource Sharing (CORS) for the index.
   */
  corsOptions?: CorsOptions;
  /**
   * The suggesters for the index.
   */
  suggesters?: SearchSuggester[];
  /**
   * The analyzers for the index.
   */
  analyzers?: LexicalAnalyzer[];
  /**
   * The tokenizers for the index.
   */
  tokenizers?: LexicalTokenizer[];
  /**
   * The token filters for the index.
   */
  tokenFilters?: TokenFilter[];
  /**
   * The character filters for the index.
   */
  charFilters?: CharFilter[];
  /**
   * The normalizers for the index.
   */
  normalizers?: LexicalNormalizer[];
  /**
   * A description of an encryption key that you create in Azure Key Vault. This key is used to
   * provide an additional level of encryption-at-rest for your data when you want full assurance
   * that no one, not even Microsoft, can decrypt your data in Azure AI Search. Once you
   * have encrypted your data, it will always remain encrypted. Azure AI Search will ignore
   * attempts to set this property to null. You can change this property as needed if you want to
   * rotate your encryption key; Your data will be unaffected. Encryption with customer-managed
   * keys is not available for free search services, and is only available for paid services
   * created on or after January 1, 2019.
   */
  encryptionKey?: SearchResourceEncryptionKey;
  /**
   * The type of similarity algorithm to be used when scoring and ranking the documents matching a
   * search query. The similarity algorithm can only be defined at index creation time and cannot
   * be modified on existing indexes. If null, the ClassicSimilarity algorithm is used.
   */
  similarity?: SimilarityAlgorithm;
  /**
   * Defines parameters for a search index that influence semantic capabilities.
   */
  semanticSearch?: SemanticSearch;
  /**
   * Contains configuration options related to vector search.
   */
  vectorSearch?: VectorSearch;
  /**
   * The ETag of the index.
   */
  etag?: string;
  /** A value indicating whether the index is leveraging Purview-specific features. This property defaults to false and cannot be changed after index creation. */
  purviewEnabled?: boolean;
}

/**
 * Represents an indexer.
 */
export interface SearchIndexer {
  /**
   * The name of the indexer.
   */
  name: string;
  /**
   * The description of the indexer.
   */
  description?: string;
  /**
   * The name of the datasource from which this indexer reads data.
   */
  dataSourceName: string;
  /**
   * The name of the skillset executing with this indexer.
   */
  skillsetName?: string;
  /**
   * The name of the index to which this indexer writes data.
   */
  targetIndexName: string;
  /**
   * The schedule for this indexer.
   */
  schedule?: IndexingSchedule;
  /**
   * Parameters for indexer execution.
   */
  parameters?: IndexingParameters;
  /**
   * Defines mappings between fields in the data source and corresponding target fields in the
   * index.
   */
  fieldMappings?: FieldMapping[];
  /**
   * Output field mappings are applied after enrichment and immediately before indexing.
   */
  outputFieldMappings?: FieldMapping[];
  /**
   * A value indicating whether the indexer is disabled. Default is false. Default value: false.
   */
  isDisabled?: boolean;
  /**
   * The ETag of the indexer.
   */
  etag?: string;
  /**
   * A description of an encryption key that you create in Azure Key Vault. This key is used to
   * provide an additional level of encryption-at-rest for your indexer definition (as well as
   * indexer execution status) when you want full assurance that no one, not even Microsoft, can
   * decrypt them in Azure AI Search. Once you have encrypted your indexer definition, it
   * will always remain encrypted. Azure AI Search will ignore attempts to set this property
   * to null. You can change this property as needed if you want to rotate your encryption key;
   * Your indexer definition (and indexer execution status) will be unaffected. Encryption with
   * customer-managed keys is not available for free search services, and is only available for
   * paid services created on or after January 1, 2019.
   */
  encryptionKey?: SearchResourceEncryptionKey;
}

/**
 * A customer-managed encryption key in Azure Key Vault. Keys that you create and manage can be
 * used to encrypt or decrypt data-at-rest in Azure AI Search, such as indexes and synonym
 * maps.
 */
export interface SearchResourceEncryptionKey {
  /**
   * The name of your Azure Key Vault key to be used to encrypt your data at rest.
   */
  keyName: string;
  /**
   * The version of your Azure Key Vault key to be used to encrypt your data at rest.
   */
  keyVersion?: string;
  /**
   * The URI of your Azure Key Vault, also referred to as DNS name, that contains the key to be
   * used to encrypt your data at rest. An example URI might be
   * https://my-keyvault-name.vault.azure.net.
   */
  vaultUrl: string;
  /**
   * An AAD Application ID that was granted the required access permissions to the Azure Key Vault
   * that is to be used when encrypting your data at rest. The Application ID should not be
   * confused with the Object ID for your AAD Application.
   */
  applicationId?: string;
  /**
   * The authentication key of the specified AAD application.
   */
  applicationSecret?: string;
  /**
   * An explicit managed identity to use for this encryption key. If not specified and the access
   * credentials property is null, the system-assigned managed identity is used. On update to the
   * resource, if the explicit identity is unspecified, it remains unchanged. If "none" is specified,
   * the value of this property is cleared.
   */
  identity?: SearchIndexerDataIdentity;
}

/**
 * A list of skills.
 */
export interface SearchIndexerSkillset {
  /**
   * The name of the skillset.
   */
  name: string;
  /**
   * The description of the skillset.
   */
  description?: string;
  /**
   * A list of skills in the skillset.
   */
  skills: SearchIndexerSkill[];
  /**
   * Details about cognitive services to be used when running skills.
   */
  cognitiveServicesAccount?: CognitiveServicesAccount;
  /**
   * Definition of additional projections to azure blob, table, or files, of enriched data.
   */
  knowledgeStore?: SearchIndexerKnowledgeStore;
  /**
   *  Definition of additional projections to secondary search index(es).
   */
  indexProjection?: SearchIndexerIndexProjection;
  /**
   * The ETag of the skillset.
   */
  etag?: string;
  /**
   * A description of an encryption key that you create in Azure Key Vault. This key is used to
   * provide an additional level of encryption-at-rest for your skillset definition when you want
   * full assurance that no one, not even Microsoft, can decrypt your skillset definition in Azure
   * AI Search. Once you have encrypted your skillset definition, it will always remain
   * encrypted. Azure AI Search will ignore attempts to set this property to null. You can
   * change this property as needed if you want to rotate your encryption key; Your skillset
   * definition will be unaffected. Encryption with customer-managed keys is not available for free
   * search services, and is only available for paid services created on or after January 1, 2019.
   */
  encryptionKey?: SearchResourceEncryptionKey;
}

/**
 * Defines parameters for a search index that influence scoring in search queries.
 */
export interface ScoringProfile {
  /**
   * The name of the scoring profile.
   */
  name: string;
  /**
   * Parameters that boost scoring based on text matches in certain index fields.
   */
  textWeights?: TextWeights;
  /**
   * The collection of functions that influence the scoring of documents.
   */
  functions?: ScoringFunction[];
  /**
   * A value indicating how the results of individual scoring functions should be combined.
   * Defaults to "Sum". Ignored if there are no scoring functions. Possible values include: 'sum',
   * 'average', 'minimum', 'maximum', 'firstMatching'
   */
  functionAggregation?: ScoringFunctionAggregation;
}

/**
 * Defines values for TokenizerName.
 * @readonly
 */
export enum KnownTokenizerNames {
  /**
   * Grammar-based tokenizer that is suitable for processing most European-language documents. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/standard/ClassicTokenizer.html
   */
  Classic = "classic",
  /**
   * Tokenizes the input from an edge into n-grams of the given size(s). See
   * https://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/ngram/EdgeNGramTokenizer.html
   */
  EdgeNGram = "edgeNGram",
  /**
   * Emits the entire input as a single token. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/KeywordTokenizer.html
   */
  Keyword = "keyword_v2",
  /**
   * Divides text at non-letters. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/LetterTokenizer.html
   */
  Letter = "letter",
  /**
   * Divides text at non-letters and converts them to lower case. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/LowerCaseTokenizer.html
   */
  Lowercase = "lowercase",
  /**
   * Divides text using language-specific rules.
   */
  MicrosoftLanguageTokenizer = "microsoft_language_tokenizer",
  /**
   * Divides text using language-specific rules and reduces words to their base forms.
   */
  MicrosoftLanguageStemmingTokenizer = "microsoft_language_stemming_tokenizer",
  /**
   * Tokenizes the input into n-grams of the given size(s). See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/ngram/NGramTokenizer.html
   */
  NGram = "nGram",
  /**
   * Tokenizer for path-like hierarchies. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/path/PathHierarchyTokenizer.html
   */
  PathHierarchy = "path_hierarchy_v2",
  /**
   * Tokenizer that uses regex pattern matching to construct distinct tokens. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/pattern/PatternTokenizer.html
   */
  Pattern = "pattern",
  /**
   * Standard Lucene analyzer; Composed of the standard tokenizer, lowercase filter and stop
   * filter. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/standard/StandardTokenizer.html
   */
  Standard = "standard_v2",
  /**
   * Tokenizes urls and emails as one token. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/standard/UAX29URLEmailTokenizer.html
   */
  UaxUrlEmail = "uax_url_email",
  /**
   * Divides text at whitespace. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/WhitespaceTokenizer.html
   */
  Whitespace = "whitespace",
}

/**
 * Defines values for TokenFilterName.
 * @readonly
 */
export enum KnownTokenFilterNames {
  /**
   * A token filter that applies the Arabic normalizer to normalize the orthography. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/ar/ArabicNormalizationFilter.html
   */
  ArabicNormalization = "arabic_normalization",
  /**
   * Strips all characters after an apostrophe (including the apostrophe itself). See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/tr/ApostropheFilter.html
   */
  Apostrophe = "apostrophe",
  /**
   * Converts alphabetic, numeric, and symbolic Unicode characters which are not in the first 127
   * ASCII characters (the "Basic Latin" Unicode block) into their ASCII equivalents, if such
   * equivalents exist. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/ASCIIFoldingFilter.html
   */
  AsciiFolding = "asciifolding",
  /**
   * Forms bigrams of CJK terms that are generated from StandardTokenizer. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/cjk/CJKBigramFilter.html
   */
  CjkBigram = "cjk_bigram",
  /**
   * Normalizes CJK width differences. Folds fullwidth ASCII variants into the equivalent basic
   * Latin, and half-width Katakana variants into the equivalent Kana. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/cjk/CJKWidthFilter.html
   */
  CjkWidth = "cjk_width",
  /**
   * Removes English possessives, and dots from acronyms. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/standard/ClassicFilter.html
   */
  Classic = "classic",
  /**
   * Construct bigrams for frequently occurring terms while indexing. Single terms are still
   * indexed too, with bigrams overlaid. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/commongrams/CommonGramsFilter.html
   */
  CommonGram = "common_grams",
  /**
   * Generates n-grams of the given size(s) starting from the front or the back of an input token.
   * See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/ngram/EdgeNGramTokenFilter.html
   */
  EdgeNGram = "edgeNGram_v2",
  /**
   * Removes elisions. For example, "l'avion" (the plane) will be converted to "avion" (plane). See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/util/ElisionFilter.html
   */
  Elision = "elision",
  /**
   * Normalizes German characters according to the heuristics of the German2 snowball algorithm.
   * See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/de/GermanNormalizationFilter.html
   */
  GermanNormalization = "german_normalization",
  /**
   * Normalizes text in Hindi to remove some differences in spelling variations. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/hi/HindiNormalizationFilter.html
   */
  HindiNormalization = "hindi_normalization",
  /**
   * Normalizes the Unicode representation of text in Indian languages. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/in/IndicNormalizationFilter.html
   */
  IndicNormalization = "indic_normalization",
  /**
   * Emits each incoming token twice, once as keyword and once as non-keyword. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/KeywordRepeatFilter.html
   */
  KeywordRepeat = "keyword_repeat",
  /**
   * A high-performance kstem filter for English. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/en/KStemFilter.html
   */
  KStem = "kstem",
  /**
   * Removes words that are too long or too short. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/LengthFilter.html
   */
  Length = "length",
  /**
   * Limits the number of tokens while indexing. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/LimitTokenCountFilter.html
   */
  Limit = "limit",
  /**
   * Normalizes token text to lower case. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/LowerCaseFilter.htm
   */
  Lowercase = "lowercase",
  /**
   * Generates n-grams of the given size(s). See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/ngram/NGramTokenFilter.html
   */
  NGram = "nGram_v2",
  /**
   * Applies normalization for Persian. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/fa/PersianNormalizationFilter.html
   */
  PersianNormalization = "persian_normalization",
  /**
   * Create tokens for phonetic matches. See
   * https://lucene.apache.org/core/4_10_3/analyzers-phonetic/org/apache/lucene/analysis/phonetic/package-tree.html
   */
  Phonetic = "phonetic",
  /**
   * Uses the Porter stemming algorithm to transform the token stream. See
   * http://tartarus.org/~martin/PorterStemmer
   */
  PorterStem = "porter_stem",
  /**
   * Reverses the token string. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/reverse/ReverseStringFilter.html
   */
  Reverse = "reverse",
  /**
   * Normalizes use of the interchangeable Scandinavian characters. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/ScandinavianNormalizationFilter.html
   */
  ScandinavianNormalization = "scandinavian_normalization",
  /**
   * Folds Scandinavian characters åÅäæÄÆ-&gt;a and öÖøØ-&gt;o. It also discriminates against use
   * of double vowels aa, ae, ao, oe and oo, leaving just the first one. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/ScandinavianFoldingFilter.html
   */
  ScandinavianFoldingNormalization = "scandinavian_folding",
  /**
   * Creates combinations of tokens as a single token. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/shingle/ShingleFilter.html
   */
  Shingle = "shingle",
  /**
   * A filter that stems words using a Snowball-generated stemmer. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/snowball/SnowballFilter.html
   */
  Snowball = "snowball",
  /**
   * Normalizes the Unicode representation of Sorani text. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/ckb/SoraniNormalizationFilter.html
   */
  SoraniNormalization = "sorani_normalization",
  /**
   * Language specific stemming filter. See
   * https://learn.microsoft.com/rest/api/searchservice/Custom-analyzers-in-Azure-Search#TokenFilters
   */
  Stemmer = "stemmer",
  /**
   * Removes stop words from a token stream. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/StopFilter.html
   */
  Stopwords = "stopwords",
  /**
   * Trims leading and trailing whitespace from tokens. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/TrimFilter.html
   */
  Trim = "trim",
  /**
   * Truncates the terms to a specific length. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/TruncateTokenFilter.html
   */
  Truncate = "truncate",
  /**
   * Filters out tokens with same text as the previous token. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/miscellaneous/RemoveDuplicatesTokenFilter.html
   */
  Unique = "unique",
  /**
   * Normalizes token text to upper case. See
   * http://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/core/UpperCaseFilter.html
   */
  Uppercase = "uppercase",
  /**
   * Splits words into subwords and performs optional transformations on subword groups.
   */
  WordDelimiter = "word_delimiter",
}

/**
 * Defines values for CharFilterName.
 * @readonly
 */
export enum KnownCharFilterNames {
  /**
   * A character filter that attempts to strip out HTML constructs. See
   * https://lucene.apache.org/core/4_10_3/analyzers-common/org/apache/lucene/analysis/charfilter/HTMLStripCharFilter.html
   */
  HtmlStrip = "html_strip",
}

/**
 * Defines values for AnalyzerName.
 * See https://learn.microsoft.com/rest/api/searchservice/Language-support
 * @readonly
 */
export enum KnownAnalyzerNames {
  /**
   * Arabic
   */
  ArMicrosoft = "ar.microsoft",
  /**
   * Arabic
   */
  ArLucene = "ar.lucene",
  /**
   * Armenian
   */
  HyLucene = "hy.lucene",
  /**
   * Bangla
   */
  BnMicrosoft = "bn.microsoft",
  /**
   * Basque
   */
  EuLucene = "eu.lucene",
  /**
   * Bulgarian
   */
  BgMicrosoft = "bg.microsoft",
  /**
   * Bulgarian
   */
  BgLucene = "bg.lucene",
  /**
   * Catalan
   */
  CaMicrosoft = "ca.microsoft",
  /**
   * Catalan
   */
  CaLucene = "ca.lucene",
  /**
   * Chinese Simplified
   */
  ZhHansMicrosoft = "zh-Hans.microsoft",
  /**
   * Chinese Simplified
   */
  ZhHansLucene = "zh-Hans.lucene",
  /**
   * Chinese Traditional
   */
  ZhHantMicrosoft = "zh-Hant.microsoft",
  /**
   * Chinese Traditional
   */
  ZhHantLucene = "zh-Hant.lucene",
  /**
   * Croatian
   */
  HrMicrosoft = "hr.microsoft",
  /**
   * Czech
   */
  CsMicrosoft = "cs.microsoft",
  /**
   * Czech
   */
  CsLucene = "cs.lucene",
  /**
   * Danish
   */
  DaMicrosoft = "da.microsoft",
  /**
   * Danish
   */
  DaLucene = "da.lucene",
  /**
   * Dutch
   */
  NlMicrosoft = "nl.microsoft",
  /**
   * Dutch
   */
  NlLucene = "nl.lucene",
  /**
   * English
   */
  EnMicrosoft = "en.microsoft",
  /**
   * English
   */
  EnLucene = "en.lucene",
  /**
   * Estonian
   */
  EtMicrosoft = "et.microsoft",
  /**
   * Finnish
   */
  FiMicrosoft = "fi.microsoft",
  /**
   * Finnish
   */
  FiLucene = "fi.lucene",
  /**
   * French
   */
  FrMicrosoft = "fr.microsoft",
  /**
   * French
   */
  FrLucene = "fr.lucene",
  /**
   * Galician
   */
  GlLucene = "gl.lucene",
  /**
   * German
   */
  DeMicrosoft = "de.microsoft",
  /**
   * German
   */
  DeLucene = "de.lucene",
  /**
   * Greek
   */
  ElMicrosoft = "el.microsoft",
  /**
   * Greek
   */
  ElLucene = "el.lucene",
  /**
   * Gujarati
   */
  GuMicrosoft = "gu.microsoft",
  /**
   * Hebrew
   */
  HeMicrosoft = "he.microsoft",
  /**
   * Hindi
   */
  HiMicrosoft = "hi.microsoft",
  /**
   * Hindi
   */
  HiLucene = "hi.lucene",
  /**
   * Hungarian
   */
  HuMicrosoft = "hu.microsoft",
  /**
   * Hungarian
   */
  HuLucene = "hu.lucene",
  /**
   * Icelandic
   */
  IsMicrosoft = "is.microsoft",
  /**
   * Indonesian (Bahasa)
   */
  IdMicrosoft = "id.microsoft",
  /**
   * Indonesian (Bahasa)
   */
  IdLucene = "id.lucene",
  /**
   * Irish
   */
  GaLucene = "ga.lucene",
  /**
   * Italian
   */
  ItMicrosoft = "it.microsoft",
  /**
   * Italian
   */
  ItLucene = "it.lucene",
  /**
   * Japanese
   */
  JaMicrosoft = "ja.microsoft",
  /**
   * Japanese
   */
  JaLucene = "ja.lucene",
  /**
   * Kannada
   */
  KnMicrosoft = "kn.microsoft",
  /**
   * Korean
   */
  KoMicrosoft = "ko.microsoft",
  /**
   * Korean
   */
  KoLucene = "ko.lucene",
  /**
   * Latvian
   */
  LvMicrosoft = "lv.microsoft",
  /**
   * Latvian
   */
  LvLucene = "lv.lucene",
  /**
   * Lithuanian
   */
  LtMicrosoft = "lt.microsoft",
  /**
   * Malayalam
   */
  MlMicrosoft = "ml.microsoft",
  /**
   * Malay (Latin)
   */
  MsMicrosoft = "ms.microsoft",
  /**
   * Marathi
   */
  MrMicrosoft = "mr.microsoft",
  /**
   * Norwegian
   */
  NbMicrosoft = "nb.microsoft",
  /**
   * Norwegian
   */
  NoLucene = "no.lucene",
  /**
   * Persian
   */
  FaLucene = "fa.lucene",
  /**
   * Polish
   */
  PlMicrosoft = "pl.microsoft",
  /**
   * Polish
   */
  PlLucene = "pl.lucene",
  /**
   * Portuguese (Brazil)
   */
  PtBRMicrosoft = "pt-BR.microsoft",
  /**
   * Portuguese (Brazil)
   */
  PtBRLucene = "pt-BR.lucene",
  /**
   * Portuguese (Portugal)
   */
  PtPTMicrosoft = "pt-PT.microsoft",
  /**
   * Portuguese (Portugal)
   */
  PtPTLucene = "pt-PT.lucene",
  /**
   * Punjabi
   */ PaMicrosoft = "pa.microsoft",
  /**
   * Romanian
   */
  RoMicrosoft = "ro.microsoft",
  /**
   * Romanian
   */
  RoLucene = "ro.lucene",
  /**
   * Russian
   */
  RuMicrosoft = "ru.microsoft",
  /**
   * Russian
   */
  RuLucene = "ru.lucene",
  /**
   * Serbian (Cyrillic)
   */
  SrCyrillicMicrosoft = "sr-cyrillic.microsoft",
  /**
   * Serbian (Latin)
   */
  SrLatinMicrosoft = "sr-latin.microsoft",
  /**
   * Slovak
   */
  SkMicrosoft = "sk.microsoft",
  /**
   * Slovenian
   */
  SlMicrosoft = "sl.microsoft",
  /**
   * Spanish
   */
  EsMicrosoft = "es.microsoft",
  /**
   * Spanish
   */
  EsLucene = "es.lucene",
  /**
   * Swedish
   */
  SvMicrosoft = "sv.microsoft",
  /**
   * Swedish
   */
  SvLucene = "sv.lucene",
  /**
   * Tamil
   */
  TaMicrosoft = "ta.microsoft",
  /**
   * Telugu
   */
  TeMicrosoft = "te.microsoft",
  /**
   * Thai
   */
  ThMicrosoft = "th.microsoft",
  /**
   * Thai
   */
  ThLucene = "th.lucene",
  /**
   * Turkish
   */
  TrMicrosoft = "tr.microsoft",
  /**
   * Turkish
   */
  TrLucene = "tr.lucene",
  /**
   * Ukrainian
   */
  UkMicrosoft = "uk.microsoft",
  /**
   * Urdu
   */
  UrMicrosoft = "ur.microsoft",
  /**
   * Vietnamese
   */
  ViMicrosoft = "vi.microsoft",
  /**
   * See:
   * https://lucene.apache.org/core/6_6_1/core/org/apache/lucene/analysis/standard/StandardAnalyzer.html
   */
  StandardLucene = "standard.lucene",
  /**
   * See
   * https://lucene.apache.org/core/6_6_1/analyzers-common/org/apache/lucene/analysis/miscellaneous/ASCIIFoldingFilter.html
   */
  StandardAsciiFoldingLucene = "standardasciifolding.lucene",
  /**
   * Treats the entire content of a field as a single token. This is useful for data like zip codes,
   * ids, and some product names.
   */
  Keyword = "keyword",
  /**
   * Flexibly separates text into terms via a regular expression pattern.
   */
  Pattern = "pattern",
  /**
   * Divides text at non-letters and converts them to lower case.
   */
  Simple = "simple",
  /**
   * Divides text at non-letters; Applies the lowercase and stopword token filters.
   */
  Stop = "stop",
  /**
   * An analyzer that uses the whitespace tokenizer.
   */
  Whitespace = "whitespace",
}

/**
 * Contains the possible cases for DataChangeDetectionPolicy.
 */
export type DataChangeDetectionPolicy =
  | HighWaterMarkChangeDetectionPolicy
  | SqlIntegratedChangeTrackingPolicy;

/**
 * Contains the possible cases for SearchIndexerDataIdentity.
 */
export type SearchIndexerDataIdentity =
  | SearchIndexerDataNoneIdentity
  | SearchIndexerDataUserAssignedIdentity;

/**
 * Contains the possible cases for DataDeletionDetectionPolicy.
 */
export type DataDeletionDetectionPolicy =
  | SoftDeleteColumnDeletionDetectionPolicy
  | NativeBlobSoftDeleteDeletionDetectionPolicy;

/**
 * Represents a datasource definition, which can be used to configure an indexer.
 */
export interface SearchIndexerDataSourceConnection {
  /**
   * The name of the datasource.
   */
  name: string;
  /**
   * The description of the datasource.
   */
  description?: string;
  /**
   * The type of the datasource. Possible values include: 'AzureSql', 'CosmosDb', 'AzureBlob',
   * 'AzureTable', 'MySql', 'AdlsGen2'
   */
  type: SearchIndexerDataSourceType;
  /**
   * A specific type of the data source, in case the resource is capable of different modalities. For example, 'MongoDb' for certain 'cosmosDb' accounts.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly subType?: string;
  /**
   * The connection string for the datasource.
   */
  connectionString?: string;
  /**
   * The data container for the datasource.
   */
  container: SearchIndexerDataContainer;
  /**
   * An explicit managed identity to use for this datasource. If not specified and the connection
   * string is a managed identity, the system-assigned managed identity is used. If not specified,
   * the value remains unchanged. If "none" is specified, the value of this property is cleared.
   */
  identity?: SearchIndexerDataIdentity;
  /**
   * The data change detection policy for the datasource.
   */
  dataChangeDetectionPolicy?: DataChangeDetectionPolicy;
  /**
   * The data deletion detection policy for the datasource.
   */
  dataDeletionDetectionPolicy?: DataDeletionDetectionPolicy;
  /**
   * The ETag of the DataSource.
   */
  etag?: string;
  /**
   * A description of an encryption key that you create in Azure Key Vault. This key is used to
   * provide an additional level of encryption-at-rest for your datasource definition when you want
   * full assurance that no one, not even Microsoft, can decrypt your data source definition in
   * Azure AI Search. Once you have encrypted your data source definition, it will always
   * remain encrypted. Azure AI Search will ignore attempts to set this property to null.
   * You can change this property as needed if you want to rotate your encryption key; Your
   * datasource definition will be unaffected. Encryption with customer-managed keys is not
   * available for free search services, and is only available for paid services created on or
   * after January 1, 2019.
   */
  encryptionKey?: SearchResourceEncryptionKey;
}

/**
 * Contains configuration options related to vector search.
 */
export interface VectorSearch {
  /**
   * Defines combinations of configurations to use with vector search.
   */
  profiles?: VectorSearchProfile[];
  /**
   * Contains configuration options specific to the algorithm used during indexing and/or querying.
   */
  algorithms?: VectorSearchAlgorithmConfiguration[];
  /**
   * Contains configuration options on how to vectorize text vector queries.
   */
  vectorizers?: VectorSearchVectorizer[];
  /**
   * Contains configuration options specific to the compression method used during indexing or
   * querying.
   */
  compressions?: VectorSearchCompression[];
}

/**
 * Contains configuration options specific to the algorithm used during indexing and/or querying.
 */
export type VectorSearchAlgorithmConfiguration =
  | HnswAlgorithmConfiguration
  | ExhaustiveKnnAlgorithmConfiguration;

/**
 * Contains configuration options specific to the algorithm used during indexing and/or querying.
 */
export interface BaseVectorSearchAlgorithmConfiguration {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: VectorSearchAlgorithmKind;
  /**
   * The name to associate with this particular configuration.
   */
  name: string;
}

/**
 * Contains configuration options specific to the hnsw approximate nearest neighbors algorithm
 * used during indexing time.
 */
export type HnswAlgorithmConfiguration = BaseVectorSearchAlgorithmConfiguration & {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "hnsw";
  /**
   * Contains the parameters specific to hnsw algorithm.
   *
   */
  parameters?: HnswParameters;
};

/**
 * Contains the parameters specific to hnsw algorithm.
 */
export interface HnswParameters {
  /**
   * The number of bi-directional links created for every new element during construction.
   * Increasing this parameter value may improve recall and reduce retrieval times for datasets
   * with high intrinsic dimensionality at the expense of increased memory consumption and longer
   * indexing time.
   */
  m?: number;
  /**
   * The size of the dynamic list containing the nearest neighbors, which is used during index
   * time. Increasing this parameter may improve index quality, at the expense of increased
   * indexing time. At a certain point, increasing this parameter leads to diminishing returns.
   */
  efConstruction?: number;
  /**
   * The size of the dynamic list containing the nearest neighbors, which is used during search
   * time. Increasing this parameter may improve search results, at the expense of slower search.
   * Increasing this parameter leads to diminishing returns.
   */
  efSearch?: number;
  /**
   * The similarity metric to use for vector comparisons.
   */
  metric?: VectorSearchAlgorithmMetric;
}

/**
 * Contains configuration options specific to the exhaustive KNN algorithm used during querying,
 * which will perform brute-force search across the entire vector index.
 */
export type ExhaustiveKnnAlgorithmConfiguration = BaseVectorSearchAlgorithmConfiguration & {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "exhaustiveKnn";
  /**
   * Contains the parameters specific to exhaustive KNN algorithm.
   */
  parameters?: ExhaustiveKnnParameters;
};

/**
 * Contains the parameters specific to exhaustive KNN algorithm.
 */
export interface ExhaustiveKnnParameters {
  /**
   * The similarity metric to use for vector comparisons.
   */
  metric?: VectorSearchAlgorithmMetric;
}

/**
 * A dictionary of index projection-specific configuration properties. Each name is the name of a
 * specific property. Each value must be of a primitive type.
 */
export interface SearchIndexerIndexProjectionParameters {
  /**
   * Describes unknown properties.
   */
  [property: string]: unknown;
  /**
   * Defines behavior of the index projections in relation to the rest of the indexer.
   */
  projectionMode?: IndexProjectionMode;
}

/**
 * Definition of additional projections to secondary search indexes.
 */
export interface SearchIndexerIndexProjection {
  /**
   * A list of projections to be performed to secondary search indexes.
   */
  selectors: SearchIndexerIndexProjectionSelector[];
  /**
   * A dictionary of index projection-specific configuration properties. Each name is the name of a
   * specific property. Each value must be of a primitive type.
   */
  parameters?: SearchIndexerIndexProjectionParameters;
}

/**
 * Contains specific details for a vectorization method to be used during query time.
 */
export interface BaseVectorSearchVectorizer {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: VectorSearchVectorizerKind;
  /**
   * The name to associate with this particular vectorization method.
   */
  vectorizerName: string;
}

/**
 * Contains the parameters specific to using an Azure Open AI service for vectorization at query
 * time.
 */
export interface AzureOpenAIVectorizer extends BaseVectorSearchVectorizer {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "azureOpenAI";
  /**
   * Contains the parameters specific to Azure Open AI embedding vectorization.
   */
  parameters?: AzureOpenAIParameters;
}

/**
 * Specifies a user-defined vectorizer for generating the vector embedding of a query string.
 * Integration of an external vectorizer is achieved using the custom Web API interface of a
 * skillset.
 */
export interface WebApiVectorizer extends BaseVectorSearchVectorizer {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "customWebApi";
  /**
   * Specifies the properties of the user-defined vectorizer.
   */
  parameters?: WebApiParameters;
}

/**
 * Specifies the properties for connecting to a user-defined vectorizer.
 */
export interface WebApiParameters {
  /**
   * The URI of the Web API providing the vectorizer.
   */
  uri?: string;
  /**
   * The headers required to make the HTTP request.
   */
  httpHeaders?: { [propertyName: string]: string };
  /**
   * The method for the HTTP request.
   */
  httpMethod?: string;
  /**
   * The desired timeout for the request. Default is 30 seconds.
   */
  timeout?: string;
  /**
   * Applies to custom endpoints that connect to external code in an Azure function or some other
   * application that provides the transformations. This value should be the application ID created
   * for the function or app when it was registered with Azure Active Directory. When specified, the
   * vectorization connects to the function or app using a managed ID (either system or
   * user-assigned) of the search service and the access token of the function or app, using this
   * value as the resource id for creating the scope of the access token.
   */
  authResourceId?: string;
  /**
   * The user-assigned managed identity used for outbound connections. If an authResourceId is
   * provided and it's not specified, the system-assigned managed identity is used. On updates to
   * the indexer, if the identity is unspecified, the value remains unchanged. If set to "none", the
   * value of this property is cleared.
   */
  authIdentity?: SearchIndexerDataIdentity;
}

/**
 * Contains configuration options on how to vectorize text vector queries.
 */
export type VectorSearchVectorizer =
  | AzureMachineLearningVectorizer
  | AzureOpenAIVectorizer
  | WebApiVectorizer;

/**
 * Specifies an Azure Machine Learning endpoint deployed via the Azure AI Foundry Model Catalog for
 * generating the vector embedding of a query string.
 */
export interface AzureMachineLearningVectorizer extends BaseVectorSearchVectorizer {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "aml";
  /**
   * Specifies the properties of the AML vectorizer.
   */
  amlParameters?: AzureMachineLearningVectorizerParameters;
}

/**
 * Specifies the properties for connecting to an AML vectorizer.
 */
export type AzureMachineLearningVectorizerParameters =
  | NoAuthAzureMachineLearningVectorizerParameters
  | KeyAuthAzureMachineLearningVectorizerParameters
  | TokenAuthAzureMachineLearningVectorizerParameters;

/**
 * Specifies the properties common between all AML vectorizer auth types.
 */
export interface BaseAzureMachineLearningVectorizerParameters {
  /**
   * When specified, indicates the timeout for the http client making the API call.
   */
  timeout?: string;
  /**
   * The name of the embedding model from the Azure AI Foundry Catalog that is deployed at the
   * provided endpoint.
   */
  modelName?: AIFoundryModelCatalogName;
}

/**
 * Specifies the properties for connecting to an AML vectorizer with no authentication.
 */
export interface NoAuthAzureMachineLearningVectorizerParameters extends BaseAzureMachineLearningVectorizerParameters {
  /**
   * Indicates how the service should attempt to identify itself to the AML instance
   */
  authKind: "none";
  /**
   * The scoring URI of the AML service to which the JSON payload will be sent. Only the https URI
   * scheme is allowed.
   */
  scoringUri: string;
}

/**
 * Specifies the properties for connecting to an AML vectorizer with an authentication key.
 */
export interface KeyAuthAzureMachineLearningVectorizerParameters extends BaseAzureMachineLearningVectorizerParameters {
  /**
   * Indicates how the service should attempt to identify itself to the AML instance
   */
  authKind: "key";
  /**
   * The scoring URI of the AML service to which the JSON payload will be sent. Only the https URI
   * scheme is allowed.
   */
  scoringUri: string;
  /**
   * The key for the AML service.
   */
  apiKey: string;
}

/**
 * Specifies the properties for connecting to an AML vectorizer with a managed identity.
 */
export interface TokenAuthAzureMachineLearningVectorizerParameters extends BaseAzureMachineLearningVectorizerParameters {
  /**
   * Indicates how the service should attempt to identify itself to the AML instance
   */
  authKind: "token";
  /**
   * The Azure Resource Manager resource ID of the AML service. It should be in the format
   * subscriptions/\{guid\}/resourceGroups/\{resource-group-name\}/Microsoft.MachineLearningServices/workspaces/\{workspace-name\}/services/\{service_name\}.
   */
  resourceId: string;
  /**
   * The region the AML service is deployed in.
   */
  region?: string;
}

/**
 * Specifies the parameters for connecting to the Azure OpenAI resource.
 */
export interface AzureOpenAIParameters {
  /**
   * The resource URI of the Azure OpenAI resource.
   */
  resourceUrl?: string;
  /**
   * ID of the Azure OpenAI model deployment on the designated resource.
   */
  deploymentId?: string;
  /**
   * API key of the designated Azure OpenAI resource.
   */
  apiKey?: string;
  /**
   * The user-assigned managed identity used for outbound connections.
   */
  authIdentity?: SearchIndexerDataIdentity;
  /**
   * The name of the embedding model that is deployed at the provided deploymentId path.
   */
  modelName?: AzureOpenAIModelName;
}

/**
 * Allows you to generate a vector embedding for a given text input using the Azure OpenAI resource.
 */
export interface AzureOpenAIEmbeddingSkill extends BaseSearchIndexerSkill, AzureOpenAIParameters {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.AzureOpenAIEmbeddingSkill";
  /**
   * The number of dimensions the resulting output embeddings should have. Only supported in
   * text-embedding-3 and later models.
   */
  dimensions?: number;
}

/**
 * A dictionary of knowledge store-specific configuration properties. Each name is the name of a
 * specific property. Each value must be of a primitive type.
 */
export interface SearchIndexerKnowledgeStoreParameters {
  /**
   * Describes unknown properties. The value of an unknown property can be of "any" type.
   */
  [property: string]: unknown;
  /**
   * Whether or not projections should synthesize a generated key name if one isn't already present.
   */
  synthesizeGeneratedKeyName?: boolean;
}

/**
 * A dictionary of indexer-specific configuration properties. Each name is the name of a specific
 * property. Each value must be of a primitive type.
 */
export interface IndexingParametersConfiguration {
  /**
   * Describes unknown properties. The value of an unknown property can be of "any" type.
   */
  [property: string]: any;
  /**
   * Represents the parsing mode for indexing from an Azure blob data source.
   */
  parsingMode?: BlobIndexerParsingMode;
  /**
   * Comma-delimited list of filename extensions to ignore when processing from Azure blob storage.
   * For example, you could exclude ".png, .mp4" to skip over those files during indexing.
   */
  excludedFileNameExtensions?: string;
  /**
   * Comma-delimited list of filename extensions to select when processing from Azure blob storage.
   * For example, you could focus indexing on specific application files ".docx, .pptx, .msg" to
   * specifically include those file types.
   */
  indexedFileNameExtensions?: string;
  /**
   * For Azure blobs, set to false if you want to continue indexing when an unsupported content type
   * is encountered, and you don't know all the content types (file extensions) in advance.
   */
  failOnUnsupportedContentType?: boolean;
  /**
   * For Azure blobs, set to false if you want to continue indexing if a document fails indexing.
   */
  failOnUnprocessableDocument?: boolean;
  /**
   * For Azure blobs, set this property to true to still index storage metadata for blob content
   * that is too large to process. Oversized blobs are treated as errors by default. For limits on
   * blob size, see https://learn.microsoft.com/azure/search/search-limits-quotas-capacity.
   */
  indexStorageMetadataOnlyForOversizedDocuments?: boolean;
  /**
   * For CSV blobs, specifies a comma-delimited list of column headers, useful for mapping source
   * fields to destination fields in an index.
   */
  delimitedTextHeaders?: string;
  /**
   * For CSV blobs, specifies the end-of-line single-character delimiter for CSV files where each
   * line starts a new document (for example, "|").
   */
  delimitedTextDelimiter?: string;
  /**
   * For CSV blobs, indicates that the first (non-blank) line of each blob contains headers.
   */
  firstLineContainsHeaders?: boolean;
  /**
   * Specifies the submode that will determine whether a markdown file will be parsed into exactly
   * one search document or multiple search documents. Default is `oneToMany`.
   */
  markdownParsingSubmode?: MarkdownParsingSubmode;
  /**
   * Specifies the max header depth that will be considered while grouping markdown content. Default
   * is `h6`.
   */
  markdownHeaderDepth?: MarkdownHeaderDepth;
  /**
   * For JSON arrays, given a structured or semi-structured document, you can specify a path to the
   * array using this property.
   */
  documentRoot?: string;
  /**
   * Specifies the data to extract from Azure blob storage and tells the indexer which data to
   * extract from image content when "imageAction" is set to a value other than "none".  This
   * applies to embedded image content in a .PDF or other application, or image files such as .jpg
   * and .png, in Azure blobs.
   */
  dataToExtract?: BlobIndexerDataToExtract;
  /**
   * Determines how to process embedded images and image files in Azure blob storage.  Setting the
   * "imageAction" configuration to any value other than "none" requires that a skillset also be
   * attached to that indexer.
   */
  imageAction?: BlobIndexerImageAction;
  /**
   * If true, will create a path //document//file_data that is an object representing the original
   * file data downloaded from your blob data source.  This allows you to pass the original file
   * data to a custom skill for processing within the enrichment pipeline, or to the Document
   * Extraction skill.
   */
  allowSkillsetToReadFileData?: boolean;
  /**
   * Determines algorithm for text extraction from PDF files in Azure blob storage.
   */
  pdfTextRotationAlgorithm?: BlobIndexerPDFTextRotationAlgorithm;
  /**
   * Specifies the environment in which the indexer should execute.
   */
  executionEnvironment?: IndexerExecutionEnvironment;
  /**
   * Increases the timeout beyond the 5-minute default for Azure SQL database data sources,
   * specified in the format "hh:mm:ss".
   */
  queryTimeout?: string;
}

/**
 * Represents parameters for indexer execution.
 */
export interface IndexingParameters {
  /**
   * The number of items that are read from the data source and indexed as a single batch in order
   * to improve performance. The default depends on the data source type.
   */
  batchSize?: number;
  /**
   * The maximum number of items that can fail indexing for indexer execution to still be considered
   * successful. -1 means no limit. Default is 0.
   */
  maxFailedItems?: number;
  /**
   * The maximum number of items in a single batch that can fail indexing for the batch to still be
   * considered successful. -1 means no limit. Default is 0.
   */
  maxFailedItemsPerBatch?: number;
  /**
   * A dictionary of indexer-specific configuration properties. Each name is the name of a specific
   * property. Each value must be of a primitive type.
   */
  configuration?: IndexingParametersConfiguration;
}

/**
 * A skill looks for text from a custom, user-defined list of words and phrases.
 */
export interface CustomEntityLookupSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.CustomEntityLookupSkill";
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: CustomEntityLookupSkillLanguage;
  /**
   * Path to a JSON or CSV file containing all the target text to match against. This entity
   * definition is read at the beginning of an indexer run. Any updates to this file during an
   * indexer run will not take effect until subsequent runs. This config must be accessible over
   * HTTPS.
   */
  entitiesDefinitionUri?: string;
  /**
   * The inline CustomEntity definition.
   */
  inlineEntitiesDefinition?: CustomEntity[];
  /**
   * A global flag for CaseSensitive. If CaseSensitive is not set in CustomEntity, this value will
   * be the default value.
   */
  globalDefaultCaseSensitive?: boolean;
  /**
   * A global flag for AccentSensitive. If AccentSensitive is not set in CustomEntity, this value
   * will be the default value.
   */
  globalDefaultAccentSensitive?: boolean;
  /**
   * A global flag for FuzzyEditDistance. If FuzzyEditDistance is not set in CustomEntity, this
   * value will be the default value.
   */
  globalDefaultFuzzyEditDistance?: number;
}

/**
 * Text analytics entity recognition.
 *
 * @deprecated This skill has been deprecated.
 */
export interface EntityRecognitionSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.EntityRecognitionSkill";
  /**
   * A list of entity categories that should be extracted.
   */
  categories?: string[];
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: string;
  /**
   * Determines whether or not to include entities which are well known but don't conform to a
   * pre-defined type. If this configuration is not set (default), set to null or set to false,
   * entities which don't conform to one of the pre-defined types will not be surfaced.
   */
  includeTypelessEntities?: boolean;
  /**
   * A value between 0 and 1 that be used to only include entities whose confidence score is greater
   * than the value specified. If not set (default), or if explicitly set to null, all entities will
   * be included.
   */
  minimumPrecision?: number;
}

/**
 * A skill that analyzes image files. It extracts a rich set of visual features based on the image
 * content.
 */
export interface ImageAnalysisSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Vision.ImageAnalysisSkill";
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: ImageAnalysisSkillLanguage;
  /**
   * A list of visual features.
   */
  visualFeatures?: VisualFeature[];
  /**
   * A string indicating which domain-specific details to return.
   */
  details?: ImageDetail[];
}

/**
 * A skill that uses text analytics for key phrase extraction.
 */
export interface KeyPhraseExtractionSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.KeyPhraseExtractionSkill";
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: KeyPhraseExtractionSkillLanguage;
  /**
   * A number indicating how many key phrases to return. If absent, all identified key phrases will
   * be returned.
   */
  maxKeyPhraseCount?: number;
  /**
   * The version of the model to use when calling the Text Analytics service. It will default to the
   * latest available when not specified. We recommend you do not specify this value unless
   * absolutely necessary.
   */
  modelVersion?: string;
}

/**
 * A skill that extracts text from image files.
 */
export interface OcrSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Vision.OcrSkill";
  /**
   * A value indicating which language code to use. Default is `en`.
   */
  defaultLanguageCode?: OcrSkillLanguage;
  /**
   * A value indicating to turn orientation detection on or not. Default is false.
   */
  shouldDetectOrientation?: boolean;
  /**
   * Defines the sequence of characters to use between the lines of text recognized by the OCR
   * skill. The default value is "space".
   */
  lineEnding?: OcrLineEnding;
}

/**
 * Using the Text Analytics API, extracts personal information from an input text and gives you the
 * option of masking it.
 */
export interface PIIDetectionSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.PIIDetectionSkill";
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: string;
  /**
   * A value between 0 and 1 that be used to only include entities whose confidence score is greater
   * than the value specified. If not set (default), or if explicitly set to null, all entities will
   * be included.
   */
  minimumPrecision?: number;
  /**
   * A parameter that provides various ways to mask the personal information detected in the input
   * text. Default is 'none'.
   */
  maskingMode?: PIIDetectionSkillMaskingMode;
  /**
   * The character used to mask the text if the maskingMode parameter is set to replace. Default is
   * '*'.
   */
  maskingCharacter?: string;
  /**
   * The version of the model to use when calling the Text Analytics service. It will default to the
   * latest available when not specified. We recommend you do not specify this value unless
   * absolutely necessary.
   */
  modelVersion?: string;
  /**
   * A list of PII entity categories that should be extracted and masked.
   */
  categories?: string[];
  /**
   * If specified, will set the PII domain to include only a subset of the entity categories.
   * Possible values include: 'phi', 'none'. Default is 'none'.
   */
  domain?: string;
}

/**
 * Text analytics positive-negative sentiment analysis, scored as a floating point value in a range
 * of zero to 1.
 *
 * @deprecated This skill has been deprecated.
 */
export interface SentimentSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.SentimentSkill";
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: string;
}

/**
 * A skill to split a string into chunks of text.
 */
export interface SplitSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.SplitSkill";
  /**
   * A value indicating which language code to use. Default is `en`.
   */
  defaultLanguageCode?: SplitSkillLanguage;
  /**
   * A value indicating which split mode to perform.
   */
  textSplitMode?: TextSplitMode;
  /**
   * The desired maximum page length. Default is 10000.
   */
  maxPageLength?: number;
  /**
   * Only applicable when textSplitMode is set to 'pages'. If specified, n+1th chunk will start with
   * this number of characters/tokens from the end of the nth chunk.
   */
  pageOverlapLength?: number;
  /**
   * Only applicable when textSplitMode is set to 'pages'. If specified, the SplitSkill will
   * discontinue splitting after processing the first 'maximumPagesToTake' pages, in order to
   * improve performance when only a few initial pages are needed from each document.
   */
  maximumPagesToTake?: number;
}

/**
 * A skill to translate text from one language to another.
 */
export interface TextTranslationSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Text.TranslationSkill";
  /**
   * The language code to translate documents into for documents that don't specify the to language
   * explicitly.
   */
  defaultToLanguageCode: TextTranslationSkillLanguage;
  /**
   * The language code to translate documents from for documents that don't specify the from
   * language explicitly.
   */
  defaultFromLanguageCode?: TextTranslationSkillLanguage;
  /**
   * The language code to translate documents from when neither the fromLanguageCode input nor the
   * defaultFromLanguageCode parameter are provided, and the automatic language detection is
   * unsuccessful. Default is en.
   */
  suggestedFrom?: TextTranslationSkillLanguage;
}

/**
 * A skill that analyzes image files. It extracts a rich set of visual features based on the image
 * content.
 */
export interface ImageAnalysisSkill extends BaseSearchIndexerSkill {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  odatatype: "#Microsoft.Skills.Vision.ImageAnalysisSkill";
  /**
   * A value indicating which language code to use. Default is en.
   */
  defaultLanguageCode?: ImageAnalysisSkillLanguage;
  /**
   * A list of visual features.
   */
  visualFeatures?: VisualFeature[];
  /**
   * A string indicating which domain-specific details to return.
   */
  details?: ImageDetail[];
}

export type KnowledgeSource =
  | BaseKnowledgeSource
  | SearchIndexKnowledgeSource
  | AzureBlobKnowledgeSource
  | IndexedOneLakeKnowledgeSource
  | WebKnowledgeSource;

/**
 * Represents a knowledge source definition.
 */
export interface BaseKnowledgeSource {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "searchIndex" | "azureBlob" | "indexedOneLake" | "web";
  /**
   * The name of the knowledge source.
   */
  name: string;
  /**
   * Optional user-defined description.
   */
  description?: string;
  /**
   * The ETag of the knowledge base.
   */
  etag?: string;
  /**
   * A description of an encryption key that you create in Azure Key Vault. This key is used to provide an additional level of encryption-at-rest for your knowledge base definition when you want full assurance that no one, not even Microsoft, can decrypt them. Once you have encrypted your knowledge base definition, it will always remain encrypted. The search service will ignore attempts to set this property to null. You can change this property as needed if you want to rotate your encryption key; Your knowledge base definition will be unaffected. Encryption with customer-managed keys is not available for free search services, and is only available for paid services created on or after January 1, 2019.
   */
  encryptionKey?: SearchResourceEncryptionKey;
}

/**
 * Knowledge Source targeting a search index.
 */
export interface SearchIndexKnowledgeSource extends BaseKnowledgeSource {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "searchIndex";
  /**
   * The parameters for the knowledge source.
   */
  searchIndexParameters: SearchIndexKnowledgeSourceParameters;
}

/**
 * Configuration for Azure Blob Storage knowledge source.
 */
export interface AzureBlobKnowledgeSource extends BaseKnowledgeSource {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "azureBlob";
  /**
   * The type of the knowledge source.
   */
  azureBlobParameters: AzureBlobKnowledgeSourceParameters;
}

/**
 * Parameters for Azure Blob Storage knowledge source.
 */
export interface AzureBlobKnowledgeSourceParameters {
  /**
   * Key-based connection string or the ResourceId format if using a managed identity.
   */
  connectionString: string;
  /**
   * The name of the blob storage container.
   */
  containerName: string;
  /**
   * Optional folder path within the container.
   */
  folderPath?: string;
  /**
   * Resources created by the knowledge source.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly createdResources?: { [propertyName: string]: string };
  /** Consolidates all general ingestion settings. */
  ingestionParameters?: KnowledgeSourceIngestionParameters;
}

/**
 * Configuration for OneLake knowledge source.
 */
export interface IndexedOneLakeKnowledgeSource extends BaseKnowledgeSource {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "indexedOneLake";
  /**
   * The parameters for the OneLake knowledge source.
   */
  indexedOneLakeParameters: IndexedOneLakeKnowledgeSourceParameters;
}

/** Parameters for OneLake knowledge source. */
export interface IndexedOneLakeKnowledgeSourceParameters {
  /** OneLake workspace ID. */
  fabricWorkspaceId: string;
  /** Specifies which OneLake lakehouse to access. */
  lakehouseId: string;
  /** Optional OneLakehouse folder or shortcut to filter OneLake content. */
  targetPath?: string;
  /** Consolidates all general ingestion settings. */
  ingestionParameters?: KnowledgeSourceIngestionParameters;
  /**
   * Resources created by the knowledge source.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly createdResources?: { [propertyName: string]: string };
}

/**
 * Knowledge Source targeting web results.
 */
export interface WebKnowledgeSource extends BaseKnowledgeSource {
  /**
   * Polymorphic discriminator, which specifies the different types this object can be
   */
  kind: "web";
  /**
   * The parameters for the web knowledge source.
   */
  webParameters?: WebKnowledgeSourceParameters;
}

/** Consolidates all general ingestion settings for knowledge sources. */
export interface KnowledgeSourceIngestionParameters {
  /** An explicit identity to use for this knowledge source. */
  identity?: SearchIndexerDataIdentity;
  /** Optional vectorizer configuration for vectorizing content. */
  embeddingModel?: KnowledgeSourceVectorizer;
  /** Optional chat completion model for image verbalization or context extraction. */
  chatCompletionModel?: KnowledgeBaseModel;
  /** Indicates whether image verbalization should be disabled. Default is false. */
  disableImageVerbalization?: boolean;
  /** Optional schedule for data ingestion. */
  ingestionSchedule?: IndexingSchedule;
  /** Optional content extraction mode. Default is 'minimal'. */
  contentExtractionMode?: KnowledgeSourceContentExtractionMode;
  /** Optional AI Services configuration for content processing. */
  aiServices?: AIServices;
}

export type KnowledgeBaseModel = KnowledgeBaseAzureOpenAIModel;

/** Specifies the Azure OpenAI resource used to do query planning. */
export interface KnowledgeBaseAzureOpenAIModel extends BaseKnowledgeBaseModel {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "azureOpenAI";
  /** Contains the parameters specific to Azure OpenAI model endpoint. */
  azureOpenAIParameters: AzureOpenAIParameters;
}

export type KnowledgeSourceVectorizer = KnowledgeSourceAzureOpenAIVectorizer;

/** Specifies the Azure OpenAI resource used to vectorize a query string. */
export interface KnowledgeSourceAzureOpenAIVectorizer extends BaseKnowledgeSourceVectorizer {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "azureOpenAI";
  /** Contains the parameters specific to Azure OpenAI embedding vectorization. */
  azureOpenAIParameters?: AzureOpenAIParameters;
}

/**
 * Contains configuration options specific to the compression method used during indexing or querying.
 */
export type VectorSearchCompression = BinaryQuantizationCompression | ScalarQuantizationCompression;

export interface CreateOrUpdateKnowledgeBaseOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}
export interface DeleteKnowledgeBaseOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}
export interface GetKnowledgeBaseOptions extends OperationOptions {}
export interface ListKnowledgeBasesOptions extends OperationOptions {}
export interface CreateKnowledgeBaseOptions extends OperationOptions {}

export interface CreateOrUpdateKnowledgeSourceOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}
export interface DeleteKnowledgeSourceOptions extends OperationOptions {
  /**
   * If set to true, Resource will be deleted only if the etag matches.
   */
  onlyIfUnchanged?: boolean;
}
export interface GetKnowledgeSourceOptions extends OperationOptions {}
export interface ListKnowledgeSourcesOptions extends OperationOptions {}
export interface CreateKnowledgeSourceOptions extends OperationOptions {}
export interface GetKnowledgeSourceStatusOptions extends OperationOptions {}

/**
 * Defines values for LexicalAnalyzerName.
 * {@link KnownLexicalAnalyzerName} can be used interchangeably with LexicalAnalyzerName,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **ar.microsoft**: Microsoft analyzer for Arabic.
 * **ar.lucene**: Lucene analyzer for Arabic.
 * **hy.lucene**: Lucene analyzer for Armenian.
 * **bn.microsoft**: Microsoft analyzer for Bangla.
 * **eu.lucene**: Lucene analyzer for Basque.
 * **bg.microsoft**: Microsoft analyzer for Bulgarian.
 * **bg.lucene**: Lucene analyzer for Bulgarian.
 * **ca.microsoft**: Microsoft analyzer for Catalan.
 * **ca.lucene**: Lucene analyzer for Catalan.
 * **zh-Hans.microsoft**: Microsoft analyzer for Chinese (Simplified).
 * **zh-Hans.lucene**: Lucene analyzer for Chinese (Simplified).
 * **zh-Hant.microsoft**: Microsoft analyzer for Chinese (Traditional).
 * **zh-Hant.lucene**: Lucene analyzer for Chinese (Traditional).
 * **hr.microsoft**: Microsoft analyzer for Croatian.
 * **cs.microsoft**: Microsoft analyzer for Czech.
 * **cs.lucene**: Lucene analyzer for Czech.
 * **da.microsoft**: Microsoft analyzer for Danish.
 * **da.lucene**: Lucene analyzer for Danish.
 * **nl.microsoft**: Microsoft analyzer for Dutch.
 * **nl.lucene**: Lucene analyzer for Dutch.
 * **en.microsoft**: Microsoft analyzer for English.
 * **en.lucene**: Lucene analyzer for English.
 * **et.microsoft**: Microsoft analyzer for Estonian.
 * **fi.microsoft**: Microsoft analyzer for Finnish.
 * **fi.lucene**: Lucene analyzer for Finnish.
 * **fr.microsoft**: Microsoft analyzer for French.
 * **fr.lucene**: Lucene analyzer for French.
 * **gl.lucene**: Lucene analyzer for Galician.
 * **de.microsoft**: Microsoft analyzer for German.
 * **de.lucene**: Lucene analyzer for German.
 * **el.microsoft**: Microsoft analyzer for Greek.
 * **el.lucene**: Lucene analyzer for Greek.
 * **gu.microsoft**: Microsoft analyzer for Gujarati.
 * **he.microsoft**: Microsoft analyzer for Hebrew.
 * **hi.microsoft**: Microsoft analyzer for Hindi.
 * **hi.lucene**: Lucene analyzer for Hindi.
 * **hu.microsoft**: Microsoft analyzer for Hungarian.
 * **hu.lucene**: Lucene analyzer for Hungarian.
 * **is.microsoft**: Microsoft analyzer for Icelandic.
 * **id.microsoft**: Microsoft analyzer for Indonesian (Bahasa).
 * **id.lucene**: Lucene analyzer for Indonesian.
 * **ga.lucene**: Lucene analyzer for Irish.
 * **it.microsoft**: Microsoft analyzer for Italian.
 * **it.lucene**: Lucene analyzer for Italian.
 * **ja.microsoft**: Microsoft analyzer for Japanese.
 * **ja.lucene**: Lucene analyzer for Japanese.
 * **kn.microsoft**: Microsoft analyzer for Kannada.
 * **ko.microsoft**: Microsoft analyzer for Korean.
 * **ko.lucene**: Lucene analyzer for Korean.
 * **lv.microsoft**: Microsoft analyzer for Latvian.
 * **lv.lucene**: Lucene analyzer for Latvian.
 * **lt.microsoft**: Microsoft analyzer for Lithuanian.
 * **ml.microsoft**: Microsoft analyzer for Malayalam.
 * **ms.microsoft**: Microsoft analyzer for Malay (Latin).
 * **mr.microsoft**: Microsoft analyzer for Marathi.
 * **nb.microsoft**: Microsoft analyzer for Norwegian (Bokmål).
 * **no.lucene**: Lucene analyzer for Norwegian.
 * **fa.lucene**: Lucene analyzer for Persian.
 * **pl.microsoft**: Microsoft analyzer for Polish.
 * **pl.lucene**: Lucene analyzer for Polish.
 * **pt-BR.microsoft**: Microsoft analyzer for Portuguese (Brazil).
 * **pt-BR.lucene**: Lucene analyzer for Portuguese (Brazil).
 * **pt-PT.microsoft**: Microsoft analyzer for Portuguese (Portugal).
 * **pt-PT.lucene**: Lucene analyzer for Portuguese (Portugal).
 * **pa.microsoft**: Microsoft analyzer for Punjabi.
 * **ro.microsoft**: Microsoft analyzer for Romanian.
 * **ro.lucene**: Lucene analyzer for Romanian.
 * **ru.microsoft**: Microsoft analyzer for Russian.
 * **ru.lucene**: Lucene analyzer for Russian.
 * **sr-cyrillic.microsoft**: Microsoft analyzer for Serbian (Cyrillic).
 * **sr-latin.microsoft**: Microsoft analyzer for Serbian (Latin).
 * **sk.microsoft**: Microsoft analyzer for Slovak.
 * **sl.microsoft**: Microsoft analyzer for Slovenian.
 * **es.microsoft**: Microsoft analyzer for Spanish.
 * **es.lucene**: Lucene analyzer for Spanish.
 * **sv.microsoft**: Microsoft analyzer for Swedish.
 * **sv.lucene**: Lucene analyzer for Swedish.
 * **ta.microsoft**: Microsoft analyzer for Tamil.
 * **te.microsoft**: Microsoft analyzer for Telugu.
 * **th.microsoft**: Microsoft analyzer for Thai.
 * **th.lucene**: Lucene analyzer for Thai.
 * **tr.microsoft**: Microsoft analyzer for Turkish.
 * **tr.lucene**: Lucene analyzer for Turkish.
 * **uk.microsoft**: Microsoft analyzer for Ukrainian.
 * **ur.microsoft**: Microsoft analyzer for Urdu.
 * **vi.microsoft**: Microsoft analyzer for Vietnamese.
 * **standard.lucene**: Standard Lucene analyzer.
 * **standardasciifolding.lucene**: Standard ASCII Folding Lucene analyzer. See https:\/\/learn.microsoft.com\/rest\/api\/searchservice\/Custom-analyzers-in-Azure-Search#Analyzers
 * **keyword**: Treats the entire content of a field as a single token. This is useful for data like zip codes, ids, and some product names. See http:\/\/lucene.apache.org\/core\/4_10_3\/analyzers-common\/org\/apache\/lucene\/analysis\/core\/KeywordAnalyzer.html
 * **pattern**: Flexibly separates text into terms via a regular expression pattern. See http:\/\/lucene.apache.org\/core\/4_10_3\/analyzers-common\/org\/apache\/lucene\/analysis\/miscellaneous\/PatternAnalyzer.html
 * **simple**: Divides text at non-letters and converts them to lower case. See http:\/\/lucene.apache.org\/core\/4_10_3\/analyzers-common\/org\/apache\/lucene\/analysis\/core\/SimpleAnalyzer.html
 * **stop**: Divides text at non-letters; Applies the lowercase and stopword token filters. See http:\/\/lucene.apache.org\/core\/4_10_3\/analyzers-common\/org\/apache\/lucene\/analysis\/core\/StopAnalyzer.html
 * **whitespace**: An analyzer that uses the whitespace tokenizer. See http:\/\/lucene.apache.org\/core\/4_10_3\/analyzers-common\/org\/apache\/lucene\/analysis\/core\/WhitespaceAnalyzer.html
 */
export type AnalyzerNames = `${KnownLexicalAnalyzerName}`;
export type BlobIndexerDataToExtract = `${KnownBlobIndexerDataToExtract}`;
export type BlobIndexerImageAction = `${KnownBlobIndexerImageAction}`;
export type BlobIndexerParsingMode = `${KnownBlobIndexerParsingMode}`;
export type BlobIndexerPDFTextRotationAlgorithm = `${KnownBlobIndexerPDFTextRotationAlgorithm}`;
export type CharFilterNames = `${KnownCharFilterName}`;
export type CustomEntityLookupSkillLanguage = `${KnownCustomEntityLookupSkillLanguage}`;
export type ImageAnalysisSkillLanguage = `${KnownImageAnalysisSkillLanguage}`;
export type ImageDetail = `${KnownImageDetail}`;
export type IndexerExecutionEnvironment = `${KnownIndexerExecutionEnvironment}`;
export type KeyPhraseExtractionSkillLanguage = `${KnownKeyPhraseExtractionSkillLanguage}`;
export type OcrSkillLanguage = `${KnownOcrSkillLanguage}`;
export type PIIDetectionSkillMaskingMode = `${KnownPIIDetectionSkillMaskingMode}`;
export type RegexFlags = `${KnownRegexFlags}`;
/**
 * Defines values for SearchFieldDataType.
 *
 * ### Known values supported by the service:
 *
 * **Edm.String**: Indicates that a field contains a string.
 *
 * **Edm.Int32**: Indicates that a field contains a 32-bit signed integer.
 *
 * **Edm.Int64**: Indicates that a field contains a 64-bit signed integer.
 *
 * **Edm.Double**: Indicates that a field contains an IEEE double-precision floating point number.
 *
 * **Edm.Boolean**: Indicates that a field contains a Boolean value (true or false).
 *
 * **Edm.DateTimeOffset**: Indicates that a field contains a date/time value, including timezone
 * information.
 *
 * **Edm.GeographyPoint**: Indicates that a field contains a geo-location in terms of longitude and
 * latitude.
 *
 * **Edm.ComplexType**: Indicates that a field contains one or more complex objects that in turn
 * have sub-fields of other types.
 *
 * **Edm.Single**: Indicates that a field contains a single-precision floating point number. This is
 * only valid when used as part of a collection type, i.e. Collection(Edm.Single).
 *
 * **Edm.Half**: Indicates that a field contains a half-precision floating point number. This is
 * only valid when used as part of a collection type, i.e. Collection(Edm.Half).
 *
 * **Edm.Int16**: Indicates that a field contains a 16-bit signed integer. This is only valid when
 * used as part of a collection type, i.e. Collection(Edm.Int16).
 *
 * **Edm.SByte**: Indicates that a field contains a 8-bit signed integer. This is only valid when
 * used as part of a collection type, i.e. Collection(Edm.SByte).
 *
 * **Edm.Byte**: Indicates that a field contains a 8-bit unsigned integer. This is only valid when
 * used as part of a collection type, i.e. Collection(Edm.Byte).
 */
export type SearchFieldDataType = Exclude<
  `${KnownSearchFieldDataType}` | `Collection(${KnownSearchFieldDataType})`,
  "Edm.ComplexType" | "Edm.Byte" | "Edm.Half" | "Edm.Int16" | "Edm.SByte" | "Edm.Single"
>;
export type SearchIndexerDataSourceType = `${KnownSearchIndexerDataSourceType}`;
// TODO: find this enum
// export type SentimentSkillLanguage = `${KnownSentimentSkillLanguage}`;
export type SplitSkillLanguage = `${KnownSplitSkillLanguage}`;
export type TextSplitMode = `${KnownTextSplitMode}`;
export type TextTranslationSkillLanguage = `${KnownTextTranslationSkillLanguage}`;
export type TokenFilterNames = `${KnownTokenFilterName}`;
export type TokenizerNames = `${KnownLexicalTokenizerName}`;
export type VectorSearchAlgorithmKind = `${KnownVectorSearchAlgorithmKind}`;
export type VectorSearchAlgorithmMetric = `${KnownVectorSearchAlgorithmMetric}`;
export type VisualFeature = `${KnownVisualFeature}`;

// Backward compatibility types for FacetResult, QueryAnswerResult, and QueryCaptionResult
// These types add index signatures to maintain backward compatibility with the old API
// where users could access dynamic properties directly (e.g., facetResult["myProperty"])
// instead of through additionalProperties (e.g., facetResult.additionalProperties?.["myProperty"])

/**
 * A single bucket of a facet query result. Reports the number of documents with a field value
 * falling within a particular range or having a particular value or interval.
 */
export interface FacetResult {
  /** The approximate count of documents falling within the bucket described by this facet. */
  readonly count?: number;
  /** The resulting total avg for the facet when a avg metric is requested. */
  readonly avg?: number;
  /** The resulting total min for the facet when a min metric is requested. */
  readonly min?: number;
  /** The resulting total max for the facet when a max metric is requested. */
  readonly max?: number;
  /** The resulting total sum for the facet when a sum metric is requested. */
  readonly sum?: number;
  /** The resulting total cardinality for the facet when a cardinality metric is requested. */
  readonly cardinality?: number;
  /** The nested facet query results for the search operation, organized as a collection of buckets for each faceted field; null if the query did not contain any nested facets. */
  readonly facets?: Record<string, FacetResult[]>;
  /** Additional properties */
  additionalProperties?: Record<string, any>;
  /**
   * Allows access to facet values directly via indexing.
   * @deprecated Use `additionalProperties` instead for accessing dynamic facet values.
   */
  [property: string]: any;
}

/**
 * An answer is a text passage extracted from the contents of the most relevant documents that
 * matched the query. Answers are extracted from the top search results. Answer candidates are
 * scored and the top answers are selected.
 */
export interface QueryAnswerResult {
  /** The score value represents how relevant the answer is to the query relative to other answers returned for the query. */
  readonly score?: number;
  /** The key of the document the answer was extracted from. */
  readonly key?: string;
  /** The text passage extracted from the document contents as the answer. */
  readonly text?: string;
  /** Same text passage as in the Text property with highlighted text phrases most relevant to the query. */
  readonly highlights?: string;
  /** Additional properties */
  additionalProperties?: Record<string, any>;
  /**
   * Allows access to additional properties directly via indexing.
   * @deprecated Use `additionalProperties` instead for accessing dynamic properties.
   */
  [property: string]: any;
}

/**
 * Captions are the most representative passages from the document relatively to the search query.
 * They are often used as document summary. Captions are only returned for queries of type
 * `semantic`.
 */
export interface QueryCaptionResult {
  /** A representative text passage extracted from the document most relevant to the search query. */
  readonly text?: string;
  /** Same text passage as in the Text property with highlighted phrases most relevant to the query. */
  readonly highlights?: string;
  /** Additional properties */
  additionalProperties?: Record<string, any>;
  /**
   * Allows access to additional properties directly via indexing.
   * @deprecated Use `additionalProperties` instead for accessing dynamic properties.
   */
  [property: string]: any;
}

// END manually modified generated interfaces
